import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';
import {
  CalendarIcon,
  Check,
  ChevronDown,
  Download,
  ExternalLink,
  Eye,
  Info,
  Link2,
  Loader2,
  LucideFileText,
  Mail,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import QuickBook from '../../assets/icons/QB.svg';
import { useDebounce, useIsMobile } from '../../helper/customHooks';
import {
  isQuickBooksBundleSetupRequired,
  isQuickBooksOnlineError,
  showQuickBooksOnlineErrorToast,
} from '../../helper/quickBooksNotifications';
import { resolveOwnerScopedAccountId } from '../../helper/accountScope';
import { resolveImageSrc } from '../../utils/image-src';
import { getAccount } from '../../api/account';
import { editClient, getClientApi } from '../../api/client';
import ClientModal from '../../containers/client/client_modal';
import {
  deleteEstimate,
  downloadEstimatePdf,
  getEstimateBuilderContext,
  getEstimatePreview,
  saveEstimate,
  sendEstimate,
  updateEstimate,
} from '../../api/estimates';
import { exportEstimateFromBuilder } from '../../api/quickbooks';
import {
  EstimateBuilderAvailableItem,
  EstimateBuilderContext,
  EstimateBuilderDraft,
  EstimateDiscount,
  EstimatePreview,
  EstimatePreviewRow,
  ProjectEstimate,
} from '../../interfaces/project-estimate';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '../UI/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { Textarea } from '../ui/textarea';
import { TruncatedTooltipText } from '../ui/truncated-tooltip-text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip-shadcn';
import { Calendar } from '../UI/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import EstimateModal from '../../modals/estimate_modal';
import BundleSetupModal from '../../modals/bundle_setup_modal';
import EstimateHistory from "./EstimateHistory";
import { createEstimateHistory, EstimateHistoryPayloadType, EstimateHistoryType } from "../../api/estimate-history";
import { downloadUrl } from "../../utils/dom";
import { trackView } from '../analytics/track_view';
import { MixpanelEvents } from '../analytics/mix_panel_events';
import { EstimateSelectorItemStatusTag } from './EstimateSelectorTags';

type BuilderMode = 'create' | 'view' | 'edit';
type SendDialogMode = 'send' | 'saveAndSend';

type EstimateBuilderSheetProps = {
  open: boolean;
  initialMode: BuilderMode;
  initialDialog?: SendDialogMode | null;
  clientId?: number;
  accountId?: number;
  estimateId?: number | null;
  client?: any;
  account?: any;
  accountUser?: any;
  existingEstimates?: ProjectEstimate[];
  onClose: () => void;
  onChanged?: () => void | Promise<void>;
  onSaved?: (savedEstimateId: number) => void | Promise<void>;
  singleUser?: boolean;
  qboConnected?: boolean;
};

type EmailDraft = {
  to: string[];
  cc: string[];
  replyTo: string[];
  subject: string;
  body: string;
};

type LoadedBuilderState = {
  mode: BuilderMode;
  baseNumber: string;
  internalName: string;
  draft: EstimateBuilderDraft;
  selectedItemIds: number[];
  selectedFeeIds: number[];
  preview: EstimatePreview | null;
  sharePublicId: string;
};

const TAX_MODE_OPTIONS = [
  { value: 'excluded', label: 'Taxes Excluded' },
  { value: 'manual', label: 'Enter Amount' },
  { value: 'calculated', label: 'Calculate' },
];

const normalizeEstimateTaxSelection = (value?: string | null) => {
  if (value === 'excluded') return 'exclude';
  if (value === 'calculated') return 'calculate';
  return value || 'exclude';
};

const getFilledStatus = (value?: string | null) =>
  String(value || '').trim() ? 'filled' : 'empty';

const resolveErrorMessage = (error: any, fallback: string) =>
  String(error?.response?.data?.message || error?.message || fallback);

const ESTIMATE_NAME_MAX_LENGTH = 200;
const TAX_DISCLAIMER_TITLE = 'Tax Calculation Disclaimer';
const PRICING_NOT_CALCULATED_TOOLTIP = 'Pricing has not yet been calculated for this item';
const TAX_DISCLAIMER_SECTIONS = [
  {
    title: 'Tax Calculation Feature',
    body: 'The Service may allow Customer to enter, store, and apply tax rates, and to designate certain line items as taxable or non-taxable for purposes of generating estimates, invoices, or other documents. The Service performs calculations based solely on information entered or selected by Customer and does not independently determine, verify, or validate any tax rate, taxability, exemption, nexus, jurisdictional rule, filing obligation, or other tax treatment.',
  },
  {
    title: 'Customer Responsibility',
    body: "Customer is solely responsible for (a) selecting the applicable tax rate, (b) determining whether any item, service, or charge is taxable, exempt, or otherwise subject to a different tax treatment, (c) confirming the accuracy of all tax-related inputs and outputs, and (d) determining, reporting, collecting, remitting, and paying any applicable taxes, fees, penalties, interest, or similar amounts.",
  },
  {
    title: 'No Tax Advice',
    body: 'The Service is provided for general administrative and estimation purposes only and does not constitute legal, tax, accounting, or other professional advice. Customer acknowledges that tax rules may vary by jurisdiction and may change over time, and that Customer should consult qualified tax or legal advisors regarding its specific circumstances.',
  },
  {
    title: 'Disclaimer of Warranties',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE AND ALL TAX-RELATED FUNCTIONALITY ARE PROVIDED "AS IS" AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND ANY WARRANTY THAT THE SERVICE WILL PRODUCE CORRECT, COMPLETE, OR COMPLIANT TAX CALCULATIONS IN EVERY INSTANCE.',
  },
  {
    title: 'Exclusion of Damages',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.',
  },
  {
    title: 'Cap on Liability',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OUR TOTAL LIABILITY ARISING OUT OF OR RELATED TO THE SERVICE WILL NOT EXCEED THE AMOUNTS PAID BY CUSTOMER TO US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR ONE HUNDRED DOLLARS, WHICHEVER IS GREATER.',
  },
  {
    title: 'Allocation of Tax Risk (Georgia)',
    body: "To the extent permitted under Georgia law, Customer agrees that any claim, loss, assessment, penalty, interest, or audit arising from Customer's selection of tax rates, designation of taxable items, use of tax outputs, or failure to collect, report, remit, or pay taxes remains Customer's responsibility, except to the extent finally determined by a court of competent jurisdiction to have been caused solely by our willful misconduct or gross negligence.",
  },
  {
    title: 'Severability',
    body: 'If any portion of this section is found unenforceable under applicable law, that portion will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will remain in full force and effect.',
  },
];

const formatAddressLinesFromSource = (source: any) => {
  if (!source) return [];
  const cityLine = [
    source.cityTown || source.shippingCityTown,
    source.stateProvince || source.province || source.shippingStateProvince,
    source.zipCode || source.shippingZIPCode,
  ].filter(Boolean).join(', ');
  return [
    source.streetAddress || source.shippingStreetAddress,
    source.unitSuite || source.shippingUnitSuite,
    cityLine,
  ].filter(Boolean).map((line) => String(line));
};

const buildCityStateZipLine = (city: any, state: any, zip: any) => {
  const normalizedCity = String(city || '').trim();
  const normalizedState = String(state || '').trim();
  let normalizedZip = String(zip || '').trim();

  if (
    normalizedState &&
    normalizedZip &&
    normalizedState.toLowerCase() === normalizedZip.toLowerCase()
  ) {
    normalizedZip = '';
  }

  if (normalizedCity && (normalizedState || normalizedZip)) {
    const stateZip = [normalizedState, normalizedZip].filter(Boolean).join(' ');
    return `${normalizedCity}, ${stateZip}`.trim();
  }

  return [normalizedCity, normalizedState, normalizedZip].filter(Boolean).join(' ').trim();
};

const normalizeAddressLineForCompare = (line: any) =>
  String(line || '')
    .toLowerCase()
    .replace(/[,\s]+/g, ' ')
    .trim();

const dedupeAddressLines = (lines: any[]) => {
  const seen = new Set<string>();
  const deduped: string[] = [];

  lines.forEach((line) => {
    const normalizedLine = normalizeAddressLineForCompare(line);
    if (!normalizedLine || seen.has(normalizedLine)) return;
    seen.add(normalizedLine);
    deduped.push(String(line || '').trim());
  });

  return deduped;
};

const excludeMatchingAddressLines = (lines: any[], excludedValues: any[]) => {
  const excluded = new Set(
    excludedValues
      .map((value) => normalizeAddressLineForCompare(value))
      .filter(Boolean)
  );

  if (!excluded.size) return lines;

  return lines.filter((line) => !excluded.has(normalizeAddressLineForCompare(line)));
};

const buildEstimateClientAddressLinesFromBillingInfo = (client: any) => {
  if (!client) return [];
  const billingDetails = getClientBillingDetailsForEstimate(client) || {};
  const lines: string[] = [];
  const companyName = String(billingDetails?.companyName || billingDetails?.company || '').trim();
  if (companyName) lines.push(companyName);
  if (billingDetails?.streetAddress) lines.push(String(billingDetails.streetAddress).trim());
  if (billingDetails?.unitSuite) lines.push(String(billingDetails.unitSuite).trim());
  const cityLine = buildCityStateZipLine(
    billingDetails?.cityTown,
    billingDetails?.stateProvince || billingDetails?.province,
    billingDetails?.zipCode || billingDetails?.ZIPCode
  );
  if (cityLine) lines.push(cityLine);
  return dedupeAddressLines(lines);
};

const buildEstimateProjectLocationAddressLines = (projectClient: any) => {
  if (!projectClient) return [];
  const lines: string[] = [];
  const companyName = String(projectClient?.companyName || projectClient?.company || '').trim();
  if (companyName) {
    lines.push(companyName);
  }
  const streetAddress = projectClient?.shippingStreetAddress || projectClient?.streetAddress;
  if (streetAddress) {
    lines.push(String(streetAddress).trim());
  }
  const unitSuite = projectClient?.shippingUnitSuite || projectClient?.unitSuite;
  if (unitSuite) {
    lines.push(String(unitSuite).trim());
  }
  const cityLine = buildCityStateZipLine(
    projectClient?.shippingCityTown || projectClient?.cityTown,
    projectClient?.shippingStateProvince || projectClient?.stateProvince || projectClient?.province,
    projectClient?.shippingZIPCode ||
      projectClient?.shippingZipCode ||
      projectClient?.zipCode ||
      projectClient?.ZIPCode
  );
  if (cityLine) lines.push(cityLine);
  return dedupeAddressLines(lines);
};

const getClientBillingDetailsForEstimate = (client: any) => {
  if (!client) return null;
  const billToParent =
    client?.billToParent === true ||
    client?.billToParent === 1 ||
    String(client?.billToParent || '').toLowerCase() === 'true';

  if (billToParent) {
    return (
      client.billingDetails ||
      client.billingInfo ||
      client.billingDetailsDirectly ||
      client.billingInfoDirectly ||
      null
    );
  }

  return (
    client.billingDetailsDirectly ||
    client.billingInfoDirectly ||
    client.billingDetails ||
    client.billingInfo ||
    null
  );
};

const getClientEmailForEstimate = (client: any) => {
  const billingDetails = getClientBillingDetailsForEstimate(client);
  return String(billingDetails?.email || client?.email || '').trim();
};

const buildEstimateClientSummaryFromClient = (client: any, fallback: any = {}) => {
  const billingDetails = getClientBillingDetailsForEstimate(client);
  const firstName = String(client?.firstName || billingDetails?.firstName || fallback?.firstName || '').trim();
  const lastName = String(client?.lastName || billingDetails?.lastName || fallback?.lastName || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName = String(client?.displayName || fallback?.displayName || '').trim();
  const company = String(client?.companyName || client?.company || fallback?.company || '').trim();
  const shippingAddressLines = formatAddressLinesFromSource({
    shippingStreetAddress: client?.shippingStreetAddress,
    shippingUnitSuite: client?.shippingUnitSuite,
    shippingCityTown: client?.shippingCityTown,
    shippingStateProvince: client?.shippingStateProvince,
    shippingZIPCode: client?.shippingZIPCode,
  });
  const fallbackClientAddressLines = formatAddressLinesFromSource({
    streetAddress: client?.streetAddress,
    unitSuite: client?.unitSuite,
    cityTown: client?.cityTown,
    stateProvince: client?.stateProvince,
    zipCode: client?.zipCode,
  });

  return {
    ...(fallback || {}),
    id: Number(client?.id || fallback?.id || 0) || null,
    name: fullName || displayName || fallback?.name || '',
    displayName,
    firstName,
    lastName,
    company,
    email: getClientEmailForEstimate(client) || fallback?.email || '',
    clientName: company || client?.company || displayName || fallback?.clientName || '',
    clientAddressLines: formatAddressLinesFromSource({
      streetAddress: client?.streetAddress,
      unitSuite: client?.unitSuite,
      cityTown: client?.cityTown,
      stateProvince: client?.stateProvince,
      zipCode: client?.zipCode,
    }),
    addressLines: shippingAddressLines.length ? shippingAddressLines : fallbackClientAddressLines,
  };
};

const normalizeClientForModal = (client: any) => {
  if (!client) return null;
  const parent = client.parent || client.parentClient || null;
  const parentClientId = toPositiveInt(client.parentClientId || parent?.id);
  const billingDetails = client.billingDetails || client.billingInfo || {};
  const billingDetailsDirectly = client.billingDetailsDirectly || client.billingInfoDirectly || {};
  return {
    ...client,
    parent,
    parentClientId: parentClientId || undefined,
    billingDetails,
    billingDetailsDirectly,
    additionalContactDetails: Array.isArray(client.additionalContactDetails)
      ? client.additionalContactDetails
      : [],
  };
};

const normalizeFetchedClientForMerge = (client: any) => {
  if (!client || client.error) return client;

  return {
    firstName: '',
    lastName: '',
    ...client,
  };
};
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const qbBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'https://app.qbo.intuit.com';
  }
  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'https://sandbox.qbo.intuit.com';
  }
  const env = (localStorage.getItem('qboEnv') || '').toLowerCase();
  return env === 'sandbox' ? 'https://sandbox.qbo.intuit.com' : 'https://app.qbo.intuit.com';
};

const buildQboEstimateUrl = (estimateId?: string | null) => {
  if (!estimateId) return '';
  return `${qbBaseUrl()}/app/estimate?txnId=${encodeURIComponent(estimateId)}`;
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value));
};

const formatEstimateRowQuantity = (row: EstimatePreviewRow) => {
  const quantity = Number(row.quantity);
  if (!Number.isFinite(quantity)) return '—';
  const formattedQuantity = Number.isInteger(quantity) ? String(quantity) : String(quantity);
  return typeof row.isCom === 'boolean' ? `${formattedQuantity} yd` : formattedQuantity;
};

const formatEstimateRowUnitPrice = (row: EstimatePreviewRow) => {
  if (row.isCom === true) return 'COM';
  return formatCurrency(row.unitPrice);
};

const formatEstimateRowAmount = (row: EstimatePreviewRow) => {
  if (row.isCom === true) return 'COM';
  return formatCurrency(row.amount);
};

const normalizePreviewDescriptionLine = (value: any) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isPgBuildComponentRow = (row?: EstimatePreviewRow | null) => {
  const rowId = String(row?.id || '').toLowerCase();
  const rowType = String(row?.rowType || '').toLowerCase();
  return rowType === 'component' && rowId.startsWith('pg-build-component-');
};

const buildPreviewRowDescriptionForDisplay = (
  row?: EstimatePreviewRow | null,
  rawDescription?: string | null
) => {
  const description = String(rawDescription || '').trim();
  if (!description || !isPgBuildComponentRow(row)) return description;

  const category = String(row?.qboType || '').trim();
  if (!category) return description;

  const prefixMatch = description.match(/^([^:]+):(.*)$/);
  if (!prefixMatch) return description;

  const prefix = String(prefixMatch[1] || '').trim().toLowerCase();
  const categoryLower = category.toLowerCase();
  if (prefix !== categoryLower) return description;

  const trimmedTail = String(prefixMatch[2] || '').trim();
  return trimmedTail || category;
};

const buildPgBuildComponentHeading = (row?: EstimatePreviewRow | null) => {
  const categoryLabel = String(row?.itemLabel || row?.qboType || '').trim();
  const rawDescription = String(row?.descriptionShort || row?.description || '').trim();
  if (!rawDescription) return categoryLabel || '—';

  const prefixMatch = rawDescription.match(/^([^:]+):(.*)$/);
  const normalizedCategory = categoryLabel.toLowerCase();
  const normalizedQboType = String(row?.qboType || '').trim().toLowerCase();
  let detailLabel = rawDescription;

  if (prefixMatch) {
    const prefix = String(prefixMatch[1] || '').trim().toLowerCase();
    const tail = String(prefixMatch[2] || '').trim();
    if (tail && (prefix === normalizedCategory || prefix === normalizedQboType)) {
      detailLabel = tail;
    }
  }

  if (!categoryLabel) return detailLabel || '—';
  if (!detailLabel) return categoryLabel;

  const normalizedDetail = detailLabel.toLowerCase();
  if (normalizedDetail === normalizedCategory || normalizedDetail === normalizedQboType) {
    return categoryLabel;
  }

  return `${categoryLabel} / ${detailLabel}`;
};

const resolvePreviewRowTypeTitle = (row?: EstimatePreviewRow | null) => {
  const qboType = String(row?.qboType || '').toLowerCase();
  if (qboType.includes('hardware')) return 'HARDWARE';
  if (qboType.includes('headrail')) return 'HEADRAIL';
  if (qboType.includes('parts') || qboType.includes('supplies')) {
    return String(row?.displayTypeLabel || row?.qboItemName || '').trim() || 'Parts & Supplies';
  }
  return '';
};

const isBundledItemRow = (row: EstimatePreviewRow) => {
  return row?.isBundleParent === true;
};

const buildBundledItemDescriptionShort = (row: EstimatePreviewRow, rows: EstimatePreviewRow[] = []) => {
  const fullDescription = String(row.description || '').trim();
  if (!fullDescription) return fullDescription;
  const rowOrderId = Number(row.orderId || 0);
  if (row.rowType !== 'item' || rowOrderId <= 0) return fullDescription;

  const childRows = rows.filter(
    (candidate) =>
      String(candidate.id) !== String(row.id) &&
      Number(candidate.orderId || 0) === rowOrderId &&
      candidate.rowType !== 'item'
  );
  if (!childRows.length) return fullDescription;

  const duplicatedLines = new Set<string>();
  childRows.forEach((childRow) => {
    [childRow.itemLabel, childRow.description].forEach((textBlock) => {
      String(textBlock || '')
        .split('\n')
        .map((line) => normalizePreviewDescriptionLine(line))
        .filter(Boolean)
        .forEach((line) => duplicatedLines.add(line));
    });
  });

  const shortLines = fullDescription
    .split('\n')
    .map((line) => String(line || '').trim())
    .filter((line) => {
      const normalizedLine = normalizePreviewDescriptionLine(line);
      if (!normalizedLine) return false;
      return !duplicatedLines.has(normalizedLine);
    });

  const shortDescription = shortLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return shortDescription || fullDescription;
};

const removeRedundantIncludedFabricationRows = (rows: EstimatePreviewRow[] = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows;
};

const enrichPreviewRowsForBundleDescriptions = (rows: EstimatePreviewRow[] = []) => {
  const filteredRows = removeRedundantIncludedFabricationRows(rows);
  return filteredRows.map((row) => {
    if (!isBundledItemRow(row)) return row;
    const descriptionShort = buildBundledItemDescriptionShort(row, filteredRows);
    return {
      ...row,
      descriptionShort,
    };
  });
};

const sanitizeEstimatePreview = (preview?: EstimatePreview | null): EstimatePreview | null => {
  if (!preview) return null;

  return {
    ...preview,
    roomSections: Array.isArray(preview.roomSections)
      ? preview.roomSections.map((section) => ({
          ...section,
          rows: enrichPreviewRowsForBundleDescriptions(section.rows || []),
        }))
      : [],
    projectFees: enrichPreviewRowsForBundleDescriptions(preview.projectFees || []),
  };
};

const applyDraftTaxToPreview = ({
  preview,
  taxMode,
  manualTaxAmount,
  calculatedTaxAmount,
  taxPercent,
  taxableLineIds,
  taxableSubtotal,
  discount,
}: {
  preview?: EstimatePreview | null;
  taxMode?: string;
  manualTaxAmount?: number;
  calculatedTaxAmount?: number;
  taxPercent?: number;
  taxableLineIds?: string[];
  taxableSubtotal?: number;
  discount?: EstimateDiscount | null;
}): EstimatePreview | null => {
  if (!preview) return null;

  const normalizedTaxMode = String(taxMode || preview.taxMode || 'excluded');
  const normalizedTaxableLineIds = normalizeStringArray(
    taxableLineIds || preview.taxableLineIds || []
  );
  const taxableLineIdSet = new Set(normalizedTaxableLineIds.map((value) => String(value)));
  const applyTaxSelectionToRow = (row: EstimatePreviewRow): EstimatePreviewRow => {
    if (!row?.taxableEligible) {
      return {
        ...row,
        taxableChecked: false,
      };
    }
    return {
      ...row,
      taxableChecked: taxableLineIdSet.has(String(row.id)),
    };
  };
  const roomSectionsWithTaxSelection = Array.isArray(preview.roomSections)
    ? preview.roomSections.map((section) => ({
        ...section,
        rows: Array.isArray(section?.rows) ? section.rows.map(applyTaxSelectionToRow) : [],
      }))
    : [];
  const projectFeesWithTaxSelection = Array.isArray(preview.projectFees)
    ? preview.projectFees.map(applyTaxSelectionToRow)
    : [];
  const itemsSubtotal = toFiniteNumber(preview?.totals?.itemsSubtotal, 0);
  const feesSubtotal = toFiniteNumber(preview?.totals?.feesSubtotal, 0);
  const subtotal = itemsSubtotal + feesSubtotal;
  const normalizedDiscount = normalizeEstimateDiscount(discount ?? preview?.discount, subtotal);
  const discountAmount = toFiniteNumber(normalizedDiscount?.amount, 0);
  const fallbackTaxableSubtotal = [
    ...roomSectionsWithTaxSelection.flatMap((section) => section.rows || []),
    ...projectFeesWithTaxSelection,
  ]
    .reduce((sum, row) => {
      return sum + getEstimateRowTaxableAmount(row, taxableLineIdSet);
    }, 0);
  const nextTaxableSubtotal = toFiniteNumber(
    taxableSubtotal,
    fallbackTaxableSubtotal
  );
  const nextTaxableSubtotalAfterDiscount = calculateDiscountedTaxableSubtotal({
    subtotal,
    taxableSubtotal: nextTaxableSubtotal,
    discount: normalizedDiscount,
  });

  let nextTaxAmount = 0;
  if (normalizedTaxMode === 'manual') {
    nextTaxAmount = toNonNegativeNumber(manualTaxAmount, 0);
  } else if (normalizedTaxMode === 'calculated') {
    nextTaxAmount = toNonNegativeNumber(calculatedTaxAmount, 0);
  }
  nextTaxAmount = roundCurrencyAmount(toFiniteNumber(nextTaxAmount, 0));
  const nextGrandTotal = roundCurrencyAmount(subtotal + discountAmount + nextTaxAmount);
  const nextTaxPercent =
    normalizedTaxMode === 'calculated' ? toNonNegativeNumber(taxPercent, 0) : null;

  return {
    ...preview,
    roomSections: roomSectionsWithTaxSelection,
    projectFees: projectFeesWithTaxSelection,
    taxMode: normalizedTaxMode,
    taxAmount: nextTaxAmount,
    discount: normalizedDiscount,
    taxPercent: nextTaxPercent,
    clientTaxRate:
      normalizedTaxMode === 'calculated'
        ? toNonNegativeNumber(taxPercent, 0)
        : toNonNegativeNumber(preview?.clientTaxRate, 0),
    taxableLineIds: normalizedTaxableLineIds,
    totals: {
      ...(preview.totals || {}),
      itemsSubtotal,
      feesSubtotal,
      discountAmount,
      taxableSubtotal: nextTaxableSubtotal,
      taxableSubtotalAfterDiscount: nextTaxableSubtotalAfterDiscount,
      taxAmount: nextTaxAmount,
      grandTotal: nextGrandTotal,
    },
  };
};

const formatDate = (value?: string | null, fallback = '—') => {
  if (!value) return fallback;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : fallback;
};

const toFiniteNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNonNegativeNumber = (value: any, fallback = 0) =>
  Math.max(0, toFiniteNumber(value, fallback));

// Snap binary float noise before rounding so half-cent boundaries resolve the
// same way as QBO's decimal arithmetic (e.g. 4.3 * 0.85 -> 365.5 cents -> 3.66,
// not the raw float 365.4999... -> 3.65). Must stay in sync with formatMoney in
// infra/aws/lambdas/estimates/shared.js and EstimatePreview.tsx.
const toDecimalCents = (value: number) => Number((toFiniteNumber(value, 0) * 100).toPrecision(12));

const roundCurrencyAmount = (value: number) => Math.round(toDecimalCents(value)) / 100;

const DISCOUNT_DEFAULT_LABEL = 'Discount';
const DECIMAL_INPUT_REGEX = /^(\d+)?(\.\d{0,2})?$/;

const normalizeDiscountType = (value?: string | null): 'amount' | 'percent' =>
  value === 'percent' ? 'percent' : 'amount';

const normalizeEstimateDiscount = (
  discount?: EstimateDiscount | null,
  subtotal = 0
): EstimateDiscount | null => {
  if (!discount || discount.enabled === false) return null;
  const type = normalizeDiscountType(discount.type);
  const rawValue =
    discount.value !== undefined && discount.value !== null && discount.value !== ''
      ? discount.value
      : discount.amount;
  const rawAbsoluteValue = Math.abs(toFiniteNumber(rawValue, 0));
  const absoluteValue =
    type === 'percent' ? Math.min(100, rawAbsoluteValue) : Math.min(Math.max(0, subtotal), rawAbsoluteValue);
  if (absoluteValue <= 0) {
    return {
      enabled: true,
      label: String(discount.label || DISCOUNT_DEFAULT_LABEL).trim() || DISCOUNT_DEFAULT_LABEL,
      type,
      value: '',
      amount: 0,
    };
  }
  // QBO rounds the discount amount to cents before using it in any downstream math.
  const amount =
    type === 'percent'
      ? roundCurrencyAmount((subtotal * absoluteValue) / 100)
      : roundCurrencyAmount(absoluteValue);

  return {
    enabled: true,
    label: String(discount.label || DISCOUNT_DEFAULT_LABEL).trim() || DISCOUNT_DEFAULT_LABEL,
    type,
    value: -absoluteValue,
    amount: -amount,
  };
};

const calculateDiscountedTaxableSubtotal = ({
  subtotal,
  taxableSubtotal,
  discount,
}: {
  subtotal: number;
  taxableSubtotal: number;
  discount?: EstimateDiscount | null;
}) => {
  const safeSubtotal = Math.max(0, toFiniteNumber(subtotal, 0));
  const safeTaxableSubtotal = Math.max(0, toFiniteNumber(taxableSubtotal, 0));
  if (safeSubtotal <= 0 || safeTaxableSubtotal <= 0 || !discount) return safeTaxableSubtotal;

  const discountType = normalizeDiscountType(discount.type);
  if (discountType === 'percent') {
    const discountPercent = Math.min(100, Math.abs(toFiniteNumber(discount.value, 0)));
    return Math.max(0, roundCurrencyAmount(safeTaxableSubtotal * (1 - discountPercent / 100)));
  }

  const discountAmount = Math.min(safeSubtotal, Math.abs(toFiniteNumber(discount.amount, 0)));
  return Math.max(
    0,
    roundCurrencyAmount(safeTaxableSubtotal * (1 - discountAmount / safeSubtotal))
  );
};

const getEstimateRowTaxableAmount = (
  row: EstimatePreviewRow,
  taxableSelectionIdSet: Set<string>
) => {
  if (!row?.taxableEligible || !taxableSelectionIdSet.has(String(row.id))) return 0;

  const explicitTaxableAmount = toFiniteNumber(row?.taxableAmount, Number.NaN);
  if (Number.isFinite(explicitTaxableAmount) && explicitTaxableAmount > 0) {
    return explicitTaxableAmount;
  }

  return toFiniteNumber(row.amount, 0);
};

const getDiscountInputValue = (discount?: EstimateDiscount | null) => {
  const value = discount?.value;
  if (value === undefined || value === null || value === '') return '';
  return String(Math.abs(toFiniteNumber(value, 0)));
};

const getEstimateSubtotal = (preview?: EstimatePreview | null) =>
  toFiniteNumber(preview?.totals?.itemsSubtotal, 0) +
  toFiniteNumber(preview?.totals?.feesSubtotal, 0);

const formatTaxRate = (value?: number | string | null) => {
  const numericValue = toFiniteNumber(value, 0);
  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(2).replace(/\.?0+$/, '');
};

const formatShortDate = (value?: string | null, fallback = '') => {
  if (!value) return fallback;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : fallback;
};

const resolveQbExportedAt = (estimate?: ProjectEstimate | null) =>
  estimate?.qbExportedAt || estimate?.estimateBody?.qbExportedAt || null;

const QuickBooksConnectTooltip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className="pointer-events-auto ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Info className="h-4 w-4" />
      </span>
    </TooltipTrigger>
    <TooltipContent side="left" sideOffset={8} className="z-[99999999] bg-white text-[#545454] shadow-lg">
      <span className="text-xs">
        <RouterLink to="/quickbooks_connect" className="text-[#3B97B1] underline">
          Link
        </RouterLink>{' '}
        your QuickBooks account to enable exporting
      </span>
    </TooltipContent>
  </Tooltip>
);

const toInputDateLabel = (value?: string | null) => {
  if (!value) return 'Select date';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : 'Select date';
};

const toCalendarDate = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = moment(value);
  if (!parsed.isValid()) return undefined;
  return parsed.toDate();
};

const sanitizeEstimateBaseNumber = (value: string) => value.replace(/[^a-z0-9]/gi, '');

const toAvailableIdSet = (entries: any[] = []) =>
  new Set(
    (entries || [])
      .map((entry) => Number(entry?.id))
      .filter((value) => Number.isInteger(value) && value > 0)
  );

const resolveEstimateHeaderStatusClassName = (status?: string | null) => {
  const normalized = String(status || 'Created').toLowerCase();
  if (normalized.includes('exported')) return 'text-[#A56909]';
  if (normalized.includes('sent')) return 'text-[#15803d]';
  if (normalized.includes('created')) return 'text-[#2563EB]';
  return 'text-muted-foreground';
};

const buildVersionedNumber = (baseNumber: string, version: number) => {
  return `${baseNumber}-${String(Math.max(1, version || 1)).padStart(2, '0')}`;
};

const resolveEstimateParts = (estimate?: ProjectEstimate | null, fallbackBaseNumber = '') => {
  const body = estimate?.estimateBody || {};
  const rawVersioned =
    body?.versionedNumber || body?.estimateNumber || (estimate as any)?.versionedNumber || '';
  const rawBase = body?.baseNumber || (estimate as any)?.baseNumber || fallbackBaseNumber;
  const explicitVersion = Number(body?.version || (estimate as any)?.version || 0);

  if (rawVersioned) {
    const match = String(rawVersioned).match(/^(.*?)-([0-9]{2,})$/);
    if (match) {
      return {
        baseNumber: String(match[1] || '').trim(),
        version: Number(match[2]) || explicitVersion || 1,
        versionedNumber: String(rawVersioned),
      };
    }
  }

  const safeBase = String(rawBase || '').trim();
  const safeVersion = explicitVersion || 1;
  return {
    baseNumber: safeBase,
    version: safeVersion,
    versionedNumber: safeBase ? buildVersionedNumber(safeBase, safeVersion) : '',
  };
};

const generateEstimatePublicId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const buildShareUrl = (publicId?: string | null) => {
  if (!publicId) return '';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  return `${String(baseUrl || '').replace(/\/$/, '')}/estimate/view/${publicId}`;
};

const getAvailableItemTypeLabel = (item: EstimateBuilderAvailableItem) =>
  String((item as any)?.itemTypeLabel || '').trim();

const getAvailableItemNameLabel = (item: EstimateBuilderAvailableItem) =>
  String((item as any)?.itemNameLabel || '').trim();

const getSelectorRoomLabel = (item: EstimateBuilderAvailableItem) => {
  const rawRoomName = String(item?.roomName || '').trim();
  if (!rawRoomName || rawRoomName.toLowerCase() === 'project items') {
    return 'Unassigned';
  }
  return rawRoomName;
};

const shouldRenderAvailableItemInSelector = (
  item: EstimateBuilderAvailableItem,
  selectedItemIds: number[] = []
) => !item?.archived || selectedItemIds.includes(Number(item?.id));

const sortAvailableItemsForBuilder = (items: EstimateBuilderAvailableItem[] = []) =>
  [...items].sort((a, b) => {
    const typeCompare = getAvailableItemTypeLabel(a).localeCompare(getAvailableItemTypeLabel(b));
    if (typeCompare !== 0) return typeCompare;

    const nameCompare = getAvailableItemNameLabel(a).localeCompare(getAvailableItemNameLabel(b));
    if (nameCompare !== 0) return nameCompare;

    return String(a.itemLabel || '').localeCompare(String(b.itemLabel || ''));
  });

const normalizeEmailList = (values: string[]) => {
  return [...new Set((values || []).map((entry) => String(entry || '').trim()).filter(Boolean))];
};

const isValidEmail = (value: string) => EMAIL_REGEX.test(String(value || '').trim());

const resolveSenderProfile = (sender?: any) => {
  const fallbackFirstName =
    typeof window !== 'undefined' ? String(localStorage.getItem('firstName') || '').trim() : '';
  const fallbackLastName =
    typeof window !== 'undefined' ? String(localStorage.getItem('lastName') || '').trim() : '';
  const fallbackEmail =
    typeof window !== 'undefined' ? String(localStorage.getItem('email') || '').trim() : '';

  const firstName = String(
    sender?.firstName || sender?.user?.firstName || fallbackFirstName || ''
  ).trim();
  const lastName = String(
    sender?.lastName || sender?.user?.lastName || fallbackLastName || ''
  ).trim();
  const email = String(sender?.email || sender?.user?.email || fallbackEmail || '').trim();

  return {
    firstName,
    lastName,
    email: isValidEmail(email) ? email : '',
  };
};

const resolveAccountEstimateSendDefaults = ({
  account,
  sender,
}: {
  account?: any;
  sender?: any;
}) => {
  const senderProfile = resolveSenderProfile(sender);
  const senderEmail = senderProfile.email;
  const accountClientFacingEmail = String(
    account?.clientFacingInfo?.email ||
      account?.facingEmail ||
      account?.billingInfo?.email ||
      account?.billingEmail ||
      ''
  ).trim();
  const accountEstimateSending = account?.accountEstimateSending || {};
  const includeSenderInCc = accountEstimateSending?.cc?.sender !== false;
  const includeSenderInReplyTo = accountEstimateSending?.replyTo?.sender !== false;
  const includeClientFacingInCc = !!accountEstimateSending?.cc?.clientFacing;
  const includeClientFacingInReplyTo = !!accountEstimateSending?.replyTo?.clientFacing;
  const additionalCcEmails = Array.isArray(account?.additionalCcEmails) ? account.additionalCcEmails : [];
  const additionalReplyToEmails = Array.isArray(account?.additionalReplyToEmails) ? account.additionalReplyToEmails : [];

  const cc = normalizeEmailList([
    ...(includeSenderInCc && isValidEmail(senderEmail) ? [senderEmail] : []),
    ...(includeClientFacingInCc && isValidEmail(accountClientFacingEmail)
      ? [accountClientFacingEmail]
      : []),
    ...additionalCcEmails,
  ]);
  const replyTo = normalizeEmailList([
    ...(includeSenderInReplyTo && isValidEmail(senderEmail) ? [senderEmail] : []),
    ...(includeClientFacingInReplyTo && isValidEmail(accountClientFacingEmail)
      ? [accountClientFacingEmail]
      : []),
    ...additionalReplyToEmails,
  ]);

  return { cc, replyTo };
};

const resolveAccountCompanyName = (account?: any) => {
  return String(
    account?.clientFacingInfo?.companyName ||
      account?.facingCompanyName ||
      account?.billingCompanyName ||
      account?.billingInfo?.company ||
      account?.companyName ||
      account?.company ||
      ''
  ).trim();
};

const toPositiveInt = (value: any): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const resolveOwnerAccountIdForEstimateEmail = ({
  account,
  accountId,
  accountUser,
}: {
  account?: any;
  accountId?: number;
  accountUser?: any;
}): number => {
  const teamContext = accountUser?.teamContext || {};
  const teamOwnerAccountId = toPositiveInt(
    teamContext?.ownerAccountId || teamContext?.owner?.accountId
  );
  if (teamOwnerAccountId > 0) return teamOwnerAccountId;

  const ownerScopedId = toPositiveInt(resolveOwnerScopedAccountId(toPositiveInt(accountId)));
  if (ownerScopedId > 0) return ownerScopedId;

  const parentAccountId = toPositiveInt(account?.parentAccountId);
  if (parentAccountId > 0) return parentAccountId;

  return toPositiveInt(account?.id || accountId);
};

const normalizeStringArray = (value: any) =>
  Array.isArray(value)
    ? [...new Set(value.map((entry) => String(entry || '').trim()).filter(Boolean))].sort()
    : [];

const collectTaxableCandidateRowsFromPreview = (preview?: EstimatePreview | null) => {
  if (!preview) return [];
  const roomRows = (preview.roomSections || []).flatMap((section) =>
    Array.isArray(section?.rows) ? section.rows : []
  );
  const projectFeeRows = Array.isArray(preview.projectFees) ? preview.projectFees : [];
  return [...roomRows, ...projectFeeRows].filter((row) => row?.taxableEligible);
};

const buildTaxableSelectionKey = (row?: EstimatePreviewRow | null) => {
  if (!row) return '';
  return [
    String(row.rowType || '').trim().toLowerCase(),
    String(row.orderId || '').trim(),
    String(row.qboType || '').trim().toLowerCase(),
    String(row.itemLabel || '').trim().toLowerCase(),
    String(row.description || '').trim().toLowerCase(),
    row.isCom === true ? 'com' : 'std',
    row.includedInPrice === true ? 'included' : 'regular',
  ]
    .join('|')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildTaxSelectionOverridesFromPreview = (
  preview: EstimatePreview | null,
  selectedTaxableLineIds: string[] = []
) => {
  const selectedIdSet = new Set(normalizeStringArray(selectedTaxableLineIds));
  return collectTaxableCandidateRowsFromPreview(preview).reduce(
    (acc: Record<string, boolean>, row) => {
      const key = buildTaxableSelectionKey(row);
      if (!key) return acc;
      acc[key] = selectedIdSet.has(String(row.id));
      return acc;
    },
    {}
  );
};

const sameStringArray = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const replaceEstimateLinkInBody = (body: string, shareUrl: string) => {
  const safeBody = String(body || '');
  const safeShareUrl = String(shareUrl || '');
  if (!safeBody || !safeShareUrl) return safeBody;
  const absoluteLinkPattern = /https?:\/\/\S*\/estimate\/view\/[A-Za-z0-9-]+/;
  if (absoluteLinkPattern.test(safeBody)) {
    return safeBody.replace(absoluteLinkPattern, safeShareUrl);
  }
  const relativeLinkPattern = /\/estimate\/view\/[A-Za-z0-9-]+/;
  if (relativeLinkPattern.test(safeBody)) {
    return safeBody.replace(relativeLinkPattern, safeShareUrl);
  }
  return safeBody;
};

const applyEstimateEmailIdentityOverrides = (
  body: string,
  clientName: string,
  companyName: string
) => {
  const lines = String(body || '').split('\n');
  if (!lines.length) return body;

  const greetingIndex = lines.findIndex((line) => String(line || '').trim().startsWith('Dear '));
  if (greetingIndex >= 0) {
    lines[greetingIndex] = `Dear ${clientName}`;
  }

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    if (String(lines[index] || '').trim()) {
      lines[index] = companyName;
      break;
    }
  }

  return lines.join('\n');
};

const buildDefaultEmailDraft = ({
  dialogMode,
  clientEmail,
  sender,
  account,
  companyName,
  clientName,
  projectName,
  versionedNumber,
  shareUrl,
  existingDraft,
}: {
  dialogMode: SendDialogMode;
  clientEmail: string;
  sender: any;
  account?: any;
  companyName: string;
  clientName: string;
  projectName: string;
  versionedNumber: string;
  shareUrl: string;
  existingDraft?: any;
}): EmailDraft => {
  const resolveBaseEstimateNumber = (value: string) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return '';
    const match = normalizedValue.match(/^(.*?)-([0-9]{2,})$/);
    return String(match?.[1] || normalizedValue).trim();
  };
  const safeCompanyName = String(companyName || '').trim() || 'Quipa';
  const safeClientName = clientName || 'Client';
  const safeProjectName = projectName || 'Project';
  const safeVersionedNumber = versionedNumber || 'ESTIMATE';
  const safeBaseEstimateNumber = resolveBaseEstimateNumber(versionedNumber) || 'ESTIMATE';
  const defaultSubject = `${safeCompanyName} - estimate ${safeBaseEstimateNumber} for ${safeProjectName}`;
  const emailRoutingDefaults = resolveAccountEstimateSendDefaults({ account, sender });

  if (existingDraft && dialogMode === 'send') {
    const existingSubject = String(existingDraft?.subject || '').trim();
    const shouldReplaceLegacySubject = / - estimate for /i.test(existingSubject);
    const existingBodyWithShareUrl = replaceEstimateLinkInBody(String(existingDraft?.body || ''), shareUrl);
    return {
      to: normalizeEmailList(existingDraft?.to || []),
      cc: normalizeEmailList([...(existingDraft?.cc || []), ...emailRoutingDefaults.cc]),
      replyTo: normalizeEmailList([...(existingDraft?.replyTo || []), ...emailRoutingDefaults.replyTo]),
      subject: !existingSubject || shouldReplaceLegacySubject ? defaultSubject : existingSubject,
      body: applyEstimateEmailIdentityOverrides(
        existingBodyWithShareUrl,
        safeClientName,
        safeCompanyName
      ),
    };
  }

  const senderProfile = resolveSenderProfile(sender);
  const senderName = [senderProfile.firstName, senderProfile.lastName].filter(Boolean).join(' ').trim();
  const senderEmail = senderProfile.email;

  return {
    to: clientEmail && isValidEmail(clientEmail) ? [clientEmail] : [],
    cc: emailRoutingDefaults.cc,
    replyTo: emailRoutingDefaults.replyTo,
    subject: defaultSubject,
    body: [
      `Dear ${safeClientName}`,
      '',
      `Please review estimate ${safeVersionedNumber} for ${safeProjectName}.`,
      'To accept or provide feedback reply to this email.',
      '',
      shareUrl,
      '',
      'Sincerely,',
      '',
      senderName,
      senderEmail,
      safeCompanyName,
    ].join('\n'),
  };
};

const EmailChipInput = ({
  label,
  description,
  values,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  description?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue('');
  }, [values.join('|')]);

  const addValue = useCallback(() => {
    const nextValue = inputValue.trim().replace(/,$/, '');
    if (!nextValue) return;
    if (!isValidEmail(nextValue)) {
      toast.error(`Invalid email: ${nextValue}`);
      return;
    }
    onChange(normalizeEmailList([...values, nextValue]));
    setInputValue('');
  }, [inputValue, onChange, values]);

  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        {description ? <div className="text-xs text-muted-foreground mt-1">{description}</div> : null}
      </div>
      {values.length ? (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full border border-input bg-muted/60 px-3 py-1 text-sm"
            >
              {value}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(values.filter((entry) => entry !== value))}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <Input
        value={inputValue}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => setInputValue(event.target.value)}
        onBlur={addValue}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addValue();
          }
        }}
      />
    </div>
  );
};

const BuilderDateField = ({
  label,
  value,
  editable,
  onChange,
}: {
  label: string;
  value?: string | null;
  editable: boolean;
  onChange: (value: string) => void;
}) => {
  if (!editable) {
    return (
      <div className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">{label}</div>
        <div className="text-[15px] text-foreground">{formatDate(value)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" type="button" className="w-full justify-start gap-2 font-normal">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {toInputDateLabel(value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={toCalendarDate(value)}
            onSelect={(date) => {
              if (!date) return;
              onChange(date.toISOString());
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const PreviewPhoto = ({ avatarId }: { avatarId?: string }) => {
  const src = resolveImageSrc(avatarId);
  return (
    <div className="h-[56px] w-[56px] overflow-hidden rounded-md border border-input bg-muted/40">
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
    </div>
  );
};

const EstimateBuilderSheet = ({
  open,
  initialMode,
  initialDialog = null,
  clientId,
  accountId,
  estimateId,
  client,
  account,
  accountUser,
  existingEstimates = [],
  onClose,
  onChanged,
  onSaved,
  singleUser,
  qboConnected = false,
}: EstimateBuilderSheetProps) => {
  const isMobile = useIsMobile(960);
  const [mode, setMode] = useState<BuilderMode>(initialMode);
  const [mobileTab, setMobileTab] = useState<'select' | 'preview'>('preview');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendDialogMode, setSendDialogMode] = useState<SendDialogMode>('send');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportSuccessModalOpen, setExportSuccessModalOpen] = useState(false);
  const [exportedQbEstimateId, setExportedQbEstimateId] = useState('');
  const [exportedQbEstimateDocNumber, setExportedQbEstimateDocNumber] = useState('');
  const [bundleSetupModalOpen, setBundleSetupModalOpen] = useState(false);
  const [bundleSetupMissingItems, setBundleSetupMissingItems] = useState<string[]>([]);
  const [bundleSetupPendingEstimate, setBundleSetupPendingEstimate] =
    useState<ProjectEstimate | null>(null);
  const [clientEditModalOpen, setClientEditModalOpen] = useState(false);
  const [clientEditLoading, setClientEditLoading] = useState(false);
  const [clientEditTabIndex, setClientEditTabIndex] = useState(0);
  const [clientEditTarget, setClientEditTarget] = useState<any>(null);
  const [editableClient, setEditableClient] = useState<any>(normalizeClientForModal(client));
  const [projectClientForEstimate, setProjectClientForEstimate] = useState<any>(
    normalizeClientForModal(client)
  );
  const [ownerAccountForEmail, setOwnerAccountForEmail] = useState<any>(account || null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [taxRateInputValue, setTaxRateInputValue] = useState<string | null>(null);
  const [manualTaxInputValue, setManualTaxInputValue] = useState<string | null>(null);
  const [discountInputValue, setDiscountInputValue] = useState<string | null>(null);
  const [taxSelectionTouched, setTaxSelectionTouched] = useState(false);
  const [context, setContext] = useState<EstimateBuilderContext | null>(null);
  const [baseNumber, setBaseNumber] = useState('');
  const [baseNumberError, setBaseNumberError] = useState('');
  const [internalName, setInternalName] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [draft, setDraft] = useState<EstimateBuilderDraft>({
    estimateDate: '',
    expirationDate: '',
    clientEmail: '',
    paymentTerms: '',
    termsAndConditions: '',
    noteToCustomer: '',
    discount: null,
    tax: {
      mode: 'excluded',
      manualAmount: 0,
      clientTaxRate: 0,
      taxPercent: null,
      taxableLineIds: [],
    },
  });
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [selectedFeeIds, setSelectedFeeIds] = useState<number[]>([]);
  const [preview, setPreview] = useState<EstimatePreview | null>(null);
  const [sharePublicId, setSharePublicId] = useState('');
  const [emailDraft, setEmailDraft] = useState<EmailDraft>({
    to: [],
    cc: [],
    replyTo: [],
    subject: '',
    body: '',
  });
  const [pdfDownloading, setPdfDownloading] = useState<boolean>(false)

  const initialStateRef = useRef<LoadedBuilderState | null>(null);
  const initialDialogHandledRef = useRef(false);
  const lastPersistedEmailRef = useRef('');
  const lastPersistedTaxRateRef = useRef<number>(0);
  const taxSelectionOverrideByKeyRef = useRef<Record<string, boolean>>({});
  const loadRequestIdRef = useRef(0);
  const setFocusFirstFieldOnClientModalRef = useRef<any>(null);
  const bundleSetupPendingEstimateRef = useRef<ProjectEstimate | null>(null);

  const existingEstimate = context?.existingEstimate || null;
  const existingBody = existingEstimate?.estimateBody || {};
  const currentEstimateParts = useMemo(
    () => resolveEstimateParts(existingEstimate, baseNumber || context?.nextBaseNumber || ''),
    [existingEstimate, baseNumber, context?.nextBaseNumber]
  );
  const selectedItemsCount = selectedItemIds.length;
  const selectedFeesCount = selectedFeeIds.length;
  const selectedLinesCount = selectedItemsCount + selectedFeesCount;
  const canSaveSelection = selectedLinesCount > 0;
  const builderLoading = open && (loading || !context);
  const shouldRenderBuilderBody = open && !builderLoading;
  const previewRequestKey = useMemo(
    () => {
      if (!open || !context) return '';
      return JSON.stringify({
        clientId,
        items: [...selectedItemIds].sort((a, b) => a - b),
        fees: [...selectedFeeIds].sort((a, b) => a - b),
        tax: draft.tax,
      });
    },
    [clientId, context, draft.tax, open, selectedFeeIds, selectedItemIds]
  );
  const debouncedPreviewKey = useDebounce(previewRequestKey, 250);

  const estimateUsageByItem = useMemo(() => {
    if (!shouldRenderBuilderBody) return new Map<number, string[]>();
    const nextMap = new Map<number, string[]>();
    existingEstimates.forEach((estimate) => {
      if (!estimate || (existingEstimate && Number(estimate.id) === Number(existingEstimate.id))) {
        return;
      }
      const parts = resolveEstimateParts(estimate);
      const label = parts.baseNumber || parts.versionedNumber;
      if (!label) return;
      (estimate.items || []).forEach((itemId) => {
        const normalizedId = Number(itemId);
        if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;
        const existingLabels = nextMap.get(normalizedId) || [];
        if (!existingLabels.includes(label)) {
          nextMap.set(normalizedId, [...existingLabels, label]);
        }
      });
    });
    return nextMap;
  }, [existingEstimate, existingEstimates, shouldRenderBuilderBody]);

  const estimateUsageByFee = useMemo(() => {
    if (!shouldRenderBuilderBody) return new Map<number, string[]>();
    const nextMap = new Map<number, string[]>();
    existingEstimates.forEach((estimate) => {
      if (!estimate || (existingEstimate && Number(estimate.id) === Number(existingEstimate.id))) {
        return;
      }
      const parts = resolveEstimateParts(estimate);
      const label = parts.baseNumber || parts.versionedNumber;
      if (!label) return;
      const feeIds = Array.isArray(estimate?.estimateBody?.selectedFeeIds)
        ? estimate.estimateBody.selectedFeeIds
        : [];
      feeIds.forEach((feeId: any) => {
        const normalizedId = Number(feeId);
        if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;
        const existingLabels = nextMap.get(normalizedId) || [];
        if (!existingLabels.includes(label)) {
          nextMap.set(normalizedId, [...existingLabels, label]);
        }
      });
    });
    return nextMap;
  }, [existingEstimate, existingEstimates, shouldRenderBuilderBody]);

  const groupedItems = useMemo(() => {
    if (!shouldRenderBuilderBody) return [];
    const map = new Map<string, EstimateBuilderAvailableItem[]>();

    (context?.availableItems || []).forEach((item) => {
      if (!shouldRenderAvailableItemInSelector(item, selectedItemIds)) {
        return;
      }
      const roomName = getSelectorRoomLabel(item);
      const existingItems = map.get(roomName) || [];
      map.set(roomName, [...existingItems, item]);
    });

    const groupedByRoom = [...map.entries()]
      .sort(([roomA], [roomB]) => roomA.localeCompare(roomB))
      .map(([roomName, items]) => ({ roomName, items: sortAvailableItemsForBuilder(items) }));

    return groupedByRoom;
  }, [context?.availableItems, selectedItemIds, shouldRenderBuilderBody]);

  const activeClient = editableClient || normalizeClientForModal(client);
  const modalClient = useMemo(
    () => normalizeClientForModal(clientEditTarget || activeClient),
    [activeClient, clientEditTarget]
  );
  const loadEstimateClientHierarchy = useCallback(
    async (seedClient?: any) => {
      const normalizedSeed = normalizeClientForModal(seedClient || client);
      const projectClientId = Number(
        context?.existingEstimate?.clientId || normalizedSeed?.id || clientId || 0
      );

      if (!projectClientId) {
        setProjectClientForEstimate(normalizedSeed);
        return {
          projectClient: normalizedSeed,
        };
      }

      let projectClient = normalizedSeed;
      try {
        const fetchedProjectClient = await getClientApi({
          clientId: projectClientId,
          accountId,
        });
        if (!fetchedProjectClient?.error) {
          projectClient = normalizeClientForModal({
            ...(normalizedSeed || {}),
            ...(normalizeFetchedClientForMerge(fetchedProjectClient) || {}),
          });
        }
      } catch (_error) {
        // Keep best-effort local data.
      }

      setProjectClientForEstimate(projectClient);

      return {
        projectClient,
      };
    },
    [accountId, client, clientId, context?.existingEstimate?.clientId]
  );

  const handleInternalNameChange = useCallback((value: string) => {
    setInternalName(value.slice(0, ESTIMATE_NAME_MAX_LENGTH));
  }, []);

  const projectName = useMemo(() => {
    return (
      activeClient?.project?.projectName ||
      activeClient?.project?.name ||
      activeClient?.projectName ||
      context?.client?.displayName ||
      'Project'
    );
  }, [activeClient, context?.client?.displayName]);

  const clientDisplayName = useMemo(() => {
    const projectClient = projectClientForEstimate || activeClient;
    const displayName = projectClient?.displayName || context?.client?.displayName || activeClient?.displayName || '';
    return displayName || 'Client';
  }, [
    activeClient,
    context?.client?.displayName,
    projectClientForEstimate,
  ]);

  const estimateBillingClient = useMemo(() => {
    const projectClient = projectClientForEstimate || activeClient;
    return projectClient || activeClient;
  }, [activeClient, projectClientForEstimate]);

  const billingClientName = useMemo(() => {
    const billingDetails = getClientBillingDetailsForEstimate(estimateBillingClient);
    const firstName = String(
      billingDetails?.firstName ||
        estimateBillingClient?.firstName ||
        activeClient?.firstName ||
        ''
    ).trim();
    const lastName = String(
      billingDetails?.lastName ||
        estimateBillingClient?.lastName ||
        activeClient?.lastName ||
        ''
    ).trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || 'Client';
  }, [
    activeClient,
    estimateBillingClient,
  ]);

  const accountCompanyName = useMemo(() => {
    return resolveAccountCompanyName(ownerAccountForEmail || account) || 'Quipa';
  }, [account, ownerAccountForEmail]);

  const estimateClientInfo = useMemo(() => {
    const billingDetails = getClientBillingDetailsForEstimate(estimateBillingClient);
    const billingFirstName = String(billingDetails?.firstName || '').trim();
    const billingLastName = String(billingDetails?.lastName || '').trim();
    const clientName = [billingFirstName, billingLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return {
      name: clientName,
      addressLines: buildEstimateClientAddressLinesFromBillingInfo(estimateBillingClient),
    };
  }, [estimateBillingClient]);

  const projectLocationInfo = useMemo(() => {
    const projectClient = projectClientForEstimate || activeClient;
    const projectName = [projectClient?.firstName, projectClient?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const name = projectName || projectClient?.displayName || context?.client?.displayName || '';
    const addressLines = excludeMatchingAddressLines(
      buildEstimateProjectLocationAddressLines({
        ...(projectClient || {}),
        companyName: projectClient?.companyName || projectClient?.company,
        shippingZIPCode:
          projectClient?.shippingZIPCode ||
          projectClient?.shippingZipCode ||
          projectClient?.zipCode ||
          projectClient?.ZIPCode,
        shippingUnitSuite: projectClient?.shippingUnitSuite || projectClient?.unitSuite,
      }),
      [name, projectClient?.displayName, context?.client?.displayName]
    );
    return {
      name,
      addressLines,
    };
  }, [
    activeClient,
    context?.client?.displayName,
    projectClientForEstimate,
  ]);

  const reloadList = useCallback(async () => {
    if (typeof onChanged === 'function') {
      await onChanged();
    }
  }, [onChanged]);

  const setDraftField = useCallback((field: keyof EstimateBuilderDraft, value: any) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const setDraftTax = useCallback((nextTax: Partial<EstimateBuilderDraft['tax']>) => {
    setDraft((prev) => ({
      ...prev,
      tax: {
        ...prev.tax,
        ...nextTax,
      },
    }));
  }, []);

  const handleAddDiscount = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      discount: {
        enabled: true,
        label: prev.discount?.label || DISCOUNT_DEFAULT_LABEL,
        type: normalizeDiscountType(prev.discount?.type),
        value: prev.discount?.value || '',
        amount: prev.discount?.amount || 0,
      },
    }));
  }, []);

  const handleRemoveDiscount = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      discount: null,
    }));
  }, []);

  const setDraftDiscount = useCallback((nextDiscount: Partial<EstimateDiscount>) => {
    setDraft((prev) => ({
      ...prev,
      discount: {
        enabled: true,
        label: prev.discount?.label || DISCOUNT_DEFAULT_LABEL,
        type: normalizeDiscountType(prev.discount?.type),
        value: prev.discount?.value || '',
        amount: prev.discount?.amount || 0,
        ...nextDiscount,
      },
    }));
  }, []);

  useEffect(() => {
    setTaxRateInputValue(null);
    setManualTaxInputValue(null);
    setDiscountInputValue(null);
  }, [draft.tax.mode]);

  const hydrateStateFromContext = useCallback(
    (builderContext: EstimateBuilderContext, nextMode: BuilderMode) => {
      const body = builderContext?.existingEstimate?.estimateBody || {};
      const availableItemIdSet = toAvailableIdSet(builderContext?.availableItems || []);
      const availableFeeIdSet = toAvailableIdSet(builderContext?.availableFees || []);
      const parts = resolveEstimateParts(
        builderContext?.existingEstimate,
        builderContext?.nextBaseNumber || ''
      );
      const nextSharePublicId =
        builderContext?.existingEstimate?.publicId ||
        body?.publicId ||
        generateEstimatePublicId();
      const nextTaxMode = body?.tax?.mode || builderContext?.defaults?.taxMode || 'excluded';
      const nextTaxPercent = toNonNegativeNumber(
        body?.tax?.taxPercent ??
        body?.tax?.ratePercent ??
        body?.tax?.clientTaxRate ??
        body?.preview?.taxPercent ??
        body?.preview?.clientTaxRate ??
        builderContext?.client?.billingSalesTax,
        0
      );
      const previewSubtotal = getEstimateSubtotal(body?.preview || null);
      const nextDiscount = normalizeEstimateDiscount(
        body?.discount || body?.preview?.discount || null,
        previewSubtotal
      );
      const nextDraft: EstimateBuilderDraft = {
        estimateDate: body?.estimateDate || builderContext?.defaults?.estimateDate || '',
        expirationDate: body?.expirationDate || builderContext?.defaults?.expirationDate || '',
        clientEmail: body?.clientEmail || builderContext?.client?.email || '',
        paymentTerms: body?.paymentTerms || builderContext?.defaults?.paymentTerms || '',
        termsAndConditions:
          body?.termsAndConditions || builderContext?.defaults?.termsAndConditions || '',
        noteToCustomer: body?.noteToCustomer || builderContext?.defaults?.noteToCustomer || '',
        discount: nextDiscount,
        tax: {
          mode: nextTaxMode,
          manualAmount: toNonNegativeNumber(
            body?.tax?.manualAmount ?? builderContext?.defaults?.manualTaxAmount,
            0
          ),
          clientTaxRate: nextTaxPercent,
          taxPercent: nextTaxMode === 'calculated' ? nextTaxPercent : body?.tax?.taxPercent ?? null,
          taxableLineIds: Array.isArray(body?.tax?.taxableLineIds) ? body.tax.taxableLineIds : [],
        },
      };
      const nextSelectedItemIds = Array.isArray(builderContext?.existingEstimate?.items)
        ? builderContext.existingEstimate.items.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
        : [];
      const nextSelectedFeeIds = Array.isArray(body?.selectedFeeIds)
        ? body.selectedFeeIds.map((value: any) => Number(value)).filter((value: number) => Number.isInteger(value) && value > 0)
        : [];
      const reconciledSelectedItemIds = nextSelectedItemIds.filter((id) => availableItemIdSet.has(id));
      const reconciledSelectedFeeIds = nextSelectedFeeIds.filter((id) => availableFeeIdSet.has(id));
      const nextPreview = sanitizeEstimatePreview(body?.preview || null);
      const loadedState: LoadedBuilderState = {
        mode: nextMode,
        baseNumber: parts.baseNumber || builderContext?.nextBaseNumber || '',
        internalName: String(body?.internalName || body?.name || body?.title || '').trim(),
        draft: nextDraft,
        selectedItemIds: reconciledSelectedItemIds,
        selectedFeeIds: reconciledSelectedFeeIds,
        preview: nextPreview,
        sharePublicId: nextSharePublicId,
      };

      setMode(nextMode);
      setBaseNumber(loadedState.baseNumber);
      setBaseNumberError('');
      setInternalName(loadedState.internalName);
      setInternalNote('');
      setDraft(loadedState.draft);
      setTaxSelectionTouched(false);
      setSelectedItemIds(loadedState.selectedItemIds);
      setSelectedFeeIds(loadedState.selectedFeeIds);
      setPreview(loadedState.preview);
      taxSelectionOverrideByKeyRef.current = buildTaxSelectionOverridesFromPreview(
        loadedState.preview,
        loadedState.draft.tax.taxableLineIds || []
      );
      setSharePublicId(loadedState.sharePublicId);
      setMobileTab(nextMode === 'create' || nextMode === 'edit' ? 'select' : 'preview');
      lastPersistedEmailRef.current = loadedState.draft.clientEmail || '';
      lastPersistedTaxRateRef.current = toFiniteNumber(loadedState.draft.tax.clientTaxRate, 0);
      initialStateRef.current = loadedState;
    },
    []
  );

  const loadContext = useCallback(
    async (targetMode: BuilderMode, targetEstimateId?: number | null) => {
      if (!open || !clientId) return;
      const requestId = loadRequestIdRef.current + 1;
      loadRequestIdRef.current = requestId;
      setLoading(true);
      try {
        const response = await getEstimateBuilderContext({
          clientId,
          accountId,
          estimateId: targetEstimateId || undefined,
        });
        if (loadRequestIdRef.current !== requestId) return;
        setContext(response as EstimateBuilderContext);
        hydrateStateFromContext(response as EstimateBuilderContext, targetMode);
      } catch (error) {
        if (loadRequestIdRef.current !== requestId) return;
        console.error('Failed to load estimate builder context:', error);
        toast.error('Failed to load estimate builder');
      } finally {
        if (loadRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [accountId, clientId, hydrateStateFromContext, open]
  );

  useEffect(() => {
    if (!open) {
      loadRequestIdRef.current += 1;
      initialDialogHandledRef.current = false;
      setClientEditModalOpen(false);
      setClientEditTarget(null);
      setLoading(false);
      setPreviewLoading(false);
      setContext(null);
      setPreview(null);
      setTaxSelectionTouched(false);
      taxSelectionOverrideByKeyRef.current = {};
      setProjectClientForEstimate(normalizeClientForModal(client));
      return;
    }
    void loadContext(initialMode, estimateId || undefined);
  }, [
    estimateId,
    initialMode,
    loadContext,
    open,
  ]);

  useEffect(() => {
    if (!open) return;
    setEditableClient(normalizeClientForModal(client));
  }, [client, open]);

  useEffect(() => {
    let active = true;

    const loadOwnerAccountForEmail = async () => {
      const ownerAccountId = resolveOwnerAccountIdForEstimateEmail({
        account,
        accountId,
        accountUser,
      });
      const currentAccountId = toPositiveInt(account?.id || accountId);

      if (!ownerAccountId || ownerAccountId === currentAccountId) {
        setOwnerAccountForEmail(account || null);
        return;
      }

      try {
        const ownerAccount = await getAccount(ownerAccountId);
        if (active) {
          setOwnerAccountForEmail(ownerAccount || account || null);
        }
      } catch (_error) {
        if (active) {
          setOwnerAccountForEmail(account || null);
        }
      }
    };

    void loadOwnerAccountForEmail();
    return () => {
      active = false;
    };
  }, [
    account,
    accountId,
    accountUser?.teamContext?.ownerAccountId,
    accountUser?.teamContext?.owner?.accountId,
  ]);

  useEffect(() => {
    if (!open || !context) return;
    void loadEstimateClientHierarchy();
  }, [context, loadEstimateClientHierarchy, open]);

  useEffect(() => {
    if (!open || !context) return;
    const savedEstimateEmail = String(context?.existingEstimate?.estimateBody?.clientEmail || '').trim();
    if (savedEstimateEmail) return;

    const fallbackContextEmail = String(context?.client?.email || '').trim();
    const billingEmail = getClientEmailForEstimate(estimateBillingClient);
    if (!billingEmail) return;

    setDraft((prev) => {
      const currentEmail = String(prev?.clientEmail || '').trim();
      if (currentEmail && currentEmail !== fallbackContextEmail) {
        return prev;
      }
      if (currentEmail === billingEmail) return prev;
      lastPersistedEmailRef.current = billingEmail;
      return {
        ...prev,
        clientEmail: billingEmail,
      };
    });
  }, [context, estimateBillingClient, open]);

  useEffect(() => {
    if (!open || !context) return;

    const availableItemIdSet = toAvailableIdSet(context.availableItems || []);
    const availableFeeIdSet = toAvailableIdSet(context.availableFees || []);

    setSelectedItemIds((prev) => prev.filter((id) => availableItemIdSet.has(id)));
    setSelectedFeeIds((prev) => prev.filter((id) => availableFeeIdSet.has(id)));
  }, [context, open]);

  const shouldUseStoredPreview =
    mode === 'view' && !!existingBody?.preview && !!initialStateRef.current?.preview;

  useEffect(() => {
    if (!open || !clientId || !context) return;
    if (!selectedItemIds.length && !selectedFeeIds.length) {
      taxSelectionOverrideByKeyRef.current = {};
      setPreview(shouldUseStoredPreview ? initialStateRef.current?.preview || null : null);
      return;
    }
    if (shouldUseStoredPreview) {
      setPreview(initialStateRef.current?.preview || null);
      return;
    }

    let active = true;
    setPreviewLoading(true);
    getEstimatePreview({
      clientId,
      accountId,
      selectedItemIds,
      selectedFeeIds,
      draft: {
        tax: {
          ...draft.tax,
          // Always let backend compute defaults for the current selection.
          // User overrides are applied locally by stable row keys.
          taxableLineIds: undefined,
        },
      },
    })
      .then((response) => {
        if (!active) return;
        const sanitizedPreview = sanitizeEstimatePreview(response as EstimatePreview);
        setPreview(sanitizedPreview);
        if (mode === 'edit' || mode === 'create') {
          const responseTaxableLineIds = new Set(
            normalizeStringArray(sanitizedPreview?.taxableLineIds)
          );
          const taxableCandidateRows = collectTaxableCandidateRowsFromPreview(sanitizedPreview);
          setDraft((prev) => {
            const nextTaxableLineIds = normalizeStringArray(
              taxableCandidateRows
                .filter((row) => {
                  const rowId = String(row.id);
                  const rowKey = buildTaxableSelectionKey(row);
                  const hasOverride =
                    !!rowKey &&
                    Object.prototype.hasOwnProperty.call(
                      taxSelectionOverrideByKeyRef.current,
                      rowKey
                    );
                  if (hasOverride) {
                    return taxSelectionOverrideByKeyRef.current[rowKey];
                  }
                  return responseTaxableLineIds.has(rowId);
                })
                .map((row) => row.id)
            );
            const nextSelectionIdSet = new Set(nextTaxableLineIds);
            taxableCandidateRows.forEach((row) => {
              const rowKey = buildTaxableSelectionKey(row);
              if (!rowKey) return;
              taxSelectionOverrideByKeyRef.current[rowKey] = nextSelectionIdSet.has(
                String(row.id)
              );
            });
            const previousTaxableIds = normalizeStringArray(prev.tax.taxableLineIds);
            if (
              sameStringArray(
                previousTaxableIds,
                nextTaxableLineIds
              )
            ) {
              return prev;
            }
            return {
              ...prev,
              tax: {
                ...prev.tax,
                taxableLineIds: nextTaxableLineIds,
              },
            };
          });
        }
      })
      .catch((error) => {
        console.error('Failed to load estimate preview:', error);
        if (active) {
          toast.error('Failed to refresh estimate preview');
        }
      })
      .finally(() => {
        if (active) {
          setPreviewLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    accountId,
    clientId,
    context,
    debouncedPreviewKey,
    mode,
    open,
    shouldUseStoredPreview,
    taxSelectionTouched,
  ]);

  useEffect(() => {
    if (!open || !context || initialDialogHandledRef.current || !initialDialog) return;
    initialDialogHandledRef.current = true;
    setSendDialogMode(initialDialog);
    const versionedNumber =
      initialDialog === 'send'
        ? currentEstimateParts.versionedNumber
        : buildVersionedNumber(baseNumber || context.nextBaseNumber || '', existingEstimate ? currentEstimateParts.version + 1 : 1);
    const shareUrl =
      buildShareUrl(existingEstimate?.publicId || sharePublicId) ||
      String(existingBody?.shareUrl || '');
    setEmailDraft(
      buildDefaultEmailDraft({
        dialogMode: initialDialog,
        clientEmail: draft.clientEmail,
        sender: accountUser,
        account,
        companyName: accountCompanyName,
        clientName: billingClientName,
        projectName,
        versionedNumber,
        shareUrl,
        existingDraft: existingBody?.emailDraft,
      })
    );
    setSendDialogOpen(true);
  }, [
    accountUser,
    account,
    accountCompanyName,
    baseNumber,
    billingClientName,
    context,
    currentEstimateParts.version,
    currentEstimateParts.versionedNumber,
    draft.clientEmail,
    existingBody,
    existingEstimate,
    initialDialog,
    open,
    projectName,
    sharePublicId,
  ]);

  const handleClose = useCallback(() => {
    if (saving || sending || deleting || exporting) return;
    setSaveDialogOpen(false);
    setSendDialogOpen(false);
    setDeleteDialogOpen(false);
    onClose();
  }, [deleting, exporting, onClose, saving, sending]);

  const isBundleSetupModalEvent = useCallback((event: any) => {
    const target = event?.target;
    return target instanceof HTMLElement && !!target.closest('.quickbooks-bundle-setup-modal');
  }, []);

  const handlePersistClientEmail = useCallback(async () => {
    const nextEmail = String(draft.clientEmail || '').trim();
    if (!activeClient?.id || !nextEmail || nextEmail === lastPersistedEmailRef.current || !isValidEmail(nextEmail)) {
      return;
    }
    setEmailSaving(true);
    try {
      const latestClient = await getClientApi({
        clientId: Number(activeClient.id),
        accountId,
      });

      if (latestClient?.error) {
        throw new Error(latestClient?.message || 'Failed to load latest client');
      }

      await editClient({
        client: {
          ...(latestClient || activeClient),
          email: nextEmail,
        },
      });
      setEditableClient((prev: any) => normalizeClientForModal({
        ...(latestClient || prev || activeClient),
        email: nextEmail,
      }));
      lastPersistedEmailRef.current = nextEmail;
      toast.success('Client email updated');
    } catch (error) {
      console.error('Failed to update client email:', error);
      toast.error('Failed to update client email');
    } finally {
      setEmailSaving(false);
    }
  }, [accountId, activeClient, draft.clientEmail]);

  const handlePersistClientTaxRate = useCallback(
    async (nextTaxRateRaw: number) => {
      const nextTaxRate = Math.max(0, toFiniteNumber(nextTaxRateRaw, 0));
      if (!activeClient?.id || nextTaxRate === lastPersistedTaxRateRef.current) {
        return;
      }
      try {
        const latestClient = await getClientApi({
          clientId: Number(activeClient.id),
          accountId,
        });

        if (latestClient?.error) {
          throw new Error(latestClient?.message || 'Failed to load latest client');
        }

        await editClient({
          client: {
            ...(latestClient || activeClient),
            billingSalesTax: nextTaxRate,
          },
        });

        setEditableClient((prev: any) =>
          normalizeClientForModal({
            ...(latestClient || prev || activeClient),
            billingSalesTax: nextTaxRate,
          })
        );
        setContext((prev) =>
          prev
            ? {
                ...prev,
                client: {
                  ...prev.client,
                  billingSalesTax: nextTaxRate,
                },
              }
            : prev
        );
        lastPersistedTaxRateRef.current = nextTaxRate;
      } catch (error) {
        console.error('Failed to update client tax rate:', error);
        toast.error('Failed to update client tax rate');
      }
    },
    [accountId, activeClient]
  );

  const loadClientForEdit = useCallback(async (targetClient?: any) => {
    const normalizedTargetClient = normalizeClientForModal(targetClient || activeClient || client);
    const editableClientId = Number(normalizedTargetClient?.id || clientId || context?.client?.id || 0);
    if (!editableClientId) {
      return normalizeClientForModal(normalizedTargetClient);
    }

    const fetchedClient = await getClientApi({ clientId: editableClientId, accountId });
    if (fetchedClient?.error) {
      throw new Error(fetchedClient?.message || 'Failed to load client');
    }

    const nextClient = normalizeClientForModal({
      ...(normalizedTargetClient || {}),
      ...(normalizeFetchedClientForMerge(fetchedClient) || {}),
    });
    return nextClient;
  }, [accountId, activeClient, client, clientId, context?.client?.id]);

  const openClientEditModal = useCallback(async (tabIndex = 0, targetClient?: any) => {
    if (mode === 'view') return;
    setClientEditLoading(true);
    try {
      const clientToEdit = await loadClientForEdit(targetClient);
      if (!clientToEdit?.id) {
        toast.error('Client details could not be loaded');
        return;
      }
      setClientEditTarget(normalizeClientForModal(clientToEdit));
      setClientEditTabIndex(tabIndex);
      setClientEditModalOpen(true);
      window.setTimeout(() => {
        if (setFocusFirstFieldOnClientModalRef.current) {
          setFocusFirstFieldOnClientModalRef.current();
        }
      }, 0);
    } catch (error) {
      console.error('Failed to load client for editing:', error);
      toast.error('Client details could not be loaded');
    } finally {
      setClientEditLoading(false);
    }
  }, [loadClientForEdit, mode]);

  const handleClientSaved = useCallback(async (savedClient: any) => {
    const normalizedSavedClient = normalizeFetchedClientForMerge(savedClient);
    const activeClientId = Number(activeClient?.id || 0);
    const editedClientId = Number(normalizedSavedClient?.id || clientEditTarget?.id || 0);
    const isEditedProjectClient =
      !!editedClientId && !!activeClientId && editedClientId === activeClientId;

    const nextProjectClient = isEditedProjectClient
      ? normalizeClientForModal({
          ...(activeClient || {}),
          ...(normalizedSavedClient || {}),
        })
      : normalizeClientForModal(activeClient);

    if (isEditedProjectClient) {
      setEditableClient(nextProjectClient);
    }

    setClientEditModalOpen(false);
    setClientEditTarget(null);

    try {
      const hierarchy = await loadEstimateClientHierarchy(nextProjectClient);
      const refreshedClient = hierarchy?.projectClient || nextProjectClient;
      const nextEmail = getClientEmailForEstimate(refreshedClient);
      const nextClientSummary = buildEstimateClientSummaryFromClient(
        refreshedClient,
        context?.client || {}
      );

      setContext((prev) =>
        prev
          ? {
              ...prev,
              client: {
                ...(prev.client || {}),
                ...nextClientSummary,
              },
            }
          : prev
      );
      setPreview((prev) =>
        prev
          ? {
              ...prev,
              client: {
                ...(prev.client || {}),
                ...nextClientSummary,
              },
            }
          : prev
      );
      setDraft((prev) => ({
        ...prev,
        clientEmail: nextEmail || prev.clientEmail,
      }));
      if (nextEmail) {
        lastPersistedEmailRef.current = nextEmail;
      }
      setEmailDraft((prev) => ({
        ...prev,
        to: nextEmail && isValidEmail(nextEmail) ? [nextEmail] : prev.to,
      }));
      if (initialStateRef.current) {
        initialStateRef.current = {
          ...initialStateRef.current,
          draft: {
            ...initialStateRef.current.draft,
            clientEmail: nextEmail || initialStateRef.current.draft.clientEmail,
          },
        };
      }
      toast.success('Client updated');
    } catch (error) {
      console.error('Failed to refresh client hierarchy after save:', error);
      toast.error('Client updated, but failed to refresh estimate details');
    }
  }, [activeClient, clientEditTarget?.id, context?.client, loadEstimateClientHierarchy]);

  const handleRoomToggle = useCallback((roomItems: EstimateBuilderAvailableItem[], checked: boolean) => {
    const eligibleIds = roomItems
      .filter((item) => item.isCalculated)
      .map((item) => Number(item.id))
      .filter((value) => Number.isInteger(value) && value > 0);
    setSelectedItemIds((prev) => {
      if (checked) {
        return [...new Set([...prev, ...eligibleIds])];
      }
      return prev.filter((id) => !eligibleIds.includes(id));
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = (context?.availableItems || [])
      .filter((item) => shouldRenderAvailableItemInSelector(item, selectedItemIds))
      .filter((item) => item.isCalculated)
      .map((item) => Number(item.id))
      .filter((value) => Number.isInteger(value) && value > 0);
    const allFeeIds = (context?.availableFees || []).map((fee) => Number(fee.id)).filter((value) => Number.isInteger(value) && value > 0);

    setSelectedItemIds([...new Set(allIds)]);
    setSelectedFeeIds([...new Set(allFeeIds)]);
  }, [context?.availableFees, context?.availableItems, selectedItemIds]);

  const handleDeselectAll = useCallback(() => {
    setSelectedItemIds([]);
    setSelectedFeeIds([]);
  }, []);

  const openSendDialog = useCallback(
    (dialogMode: SendDialogMode) => {
      const ensuredSharePublicId =
        existingEstimate?.publicId || sharePublicId || generateEstimatePublicId();
      if (!existingEstimate?.publicId && !sharePublicId) {
        setSharePublicId(ensuredSharePublicId);
      }
      const safeBaseNumber = sanitizeEstimateBaseNumber(baseNumber || context?.nextBaseNumber || '');
      const nextVersion = dialogMode === 'send'
        ? currentEstimateParts.version || 1
        : existingEstimate
          ? currentEstimateParts.version + 1
          : 1;
      const versionedNumber =
        dialogMode === 'send'
          ? currentEstimateParts.versionedNumber
          : buildVersionedNumber(safeBaseNumber, nextVersion);
      const nextShareUrl =
        buildShareUrl(ensuredSharePublicId) || String(existingBody?.shareUrl || '');
      setSendDialogMode(dialogMode);
      setEmailDraft(
        buildDefaultEmailDraft({
          dialogMode,
          clientEmail: draft.clientEmail,
          sender: accountUser,
          account,
          companyName: accountCompanyName,
          clientName: billingClientName,
          projectName,
          versionedNumber,
          shareUrl: nextShareUrl,
          existingDraft: existingBody?.emailDraft,
        })
      );
      setSendDialogOpen(true);
    },
    [
      accountUser,
      account,
      accountCompanyName,
      baseNumber,
      billingClientName,
      context?.nextBaseNumber,
      currentEstimateParts.version,
      currentEstimateParts.versionedNumber,
      draft.clientEmail,
      existingBody,
      existingEstimate,
      projectName,
      sharePublicId,
    ]
  );

  const validateBeforeSave = useCallback(() => {
    const safeBaseNumber = sanitizeEstimateBaseNumber(baseNumber || '');
    if (!safeBaseNumber) {
      setBaseNumberError('Estimate number is required.');
      return false;
    }
    if (safeBaseNumber !== baseNumber) {
      setBaseNumber(safeBaseNumber);
    }
    if (!canSaveSelection) {
      toast.error('Select at least one item or fee before saving');
      return false;
    }
    setBaseNumberError('');
    return true;
  }, [baseNumber, canSaveSelection]);

  const buildEstimateActionTrackingProps = useCallback(
    (extra: Record<string, any> = {}) => ({
      tax_selection: normalizeEstimateTaxSelection(draft.tax.mode),
      estimate_expiration: draft.expirationDate || '',
      payment_terms: getFilledStatus(draft.paymentTerms),
      terms_and_conditions: getFilledStatus(draft.termsAndConditions),
      ...extra,
    }),
    [draft.expirationDate, draft.paymentTerms, draft.tax.mode, draft.termsAndConditions]
  );

  const finalizeAfterSave = useCallback(
    async (savedEstimateId?: number | null) => {
      if (!savedEstimateId) return;
      await reloadList();
      await loadContext('view', savedEstimateId);
    },
    [loadContext, reloadList]
  );

  const handleSave = useCallback(async (
    action: EstimateHistoryType = 'saved',
    options?: { closeAfterSave?: boolean }
  ) => {
    if (!validateBeforeSave()) return null;
    setSaving(true);
    try {
      const taxableSelectionIdSet = new Set(normalizeStringArray(draft.tax.taxableLineIds || []));
      const roomRowsForSave = (preview?.roomSections || []).flatMap((section) =>
        Array.isArray(section?.rows) ? section.rows : []
      );
      const projectFeeRowsForSave = Array.isArray(preview?.projectFees) ? preview.projectFees : [];
      const visibleTaxableSubtotalForSave = [...roomRowsForSave, ...projectFeeRowsForSave].reduce(
        (sum, row) => {
          return sum + getEstimateRowTaxableAmount(row, taxableSelectionIdSet);
        },
        0
      );
      const subtotalForSave = getEstimateSubtotal(preview);
      const normalizedDiscountForSave = normalizeEstimateDiscount(draft.discount, subtotalForSave);
      const taxableSubtotalForSave = visibleTaxableSubtotalForSave;
      const discountedTaxableSubtotalForSave = calculateDiscountedTaxableSubtotal({
        subtotal: subtotalForSave,
        taxableSubtotal: taxableSubtotalForSave,
        discount: normalizedDiscountForSave,
      });
      const currentTaxRateForSave = toNonNegativeNumber(
        draft.tax.taxPercent ??
          draft.tax.clientTaxRate ??
          preview?.taxPercent ??
          preview?.clientTaxRate ??
          context?.client?.billingSalesTax,
        0
      );
      const calculatedEstimatedTaxAmountForSave = roundCurrencyAmount(
        discountedTaxableSubtotalForSave * (currentTaxRateForSave / 100)
      );
      const sanitizedPreviewForSave = applyDraftTaxToPreview({
        preview: sanitizeEstimatePreview(preview),
        taxMode: draft.tax.mode,
        manualTaxAmount: draft.tax.manualAmount,
        calculatedTaxAmount: calculatedEstimatedTaxAmountForSave,
        taxPercent: currentTaxRateForSave,
        taxableLineIds: draft.tax.taxableLineIds,
        taxableSubtotal: taxableSubtotalForSave,
        discount: normalizedDiscountForSave,
      });
      const response = existingEstimate?.id
        ? await updateEstimate({
          accountId,
          clientId,
          estimateId: existingEstimate.id,
          baseNumber: sanitizeEstimateBaseNumber(baseNumber),
          selectedItemIds,
          selectedFeeIds,
          internalName,
          internalNote,
          publicId: existingEstimate?.publicId || sharePublicId,
          draft,
          preview: sanitizedPreviewForSave,
        })
        : await saveEstimate({
          accountId,
          clientId,
          baseNumber: sanitizeEstimateBaseNumber(baseNumber),
          selectedItemIds,
          selectedFeeIds,
          internalName: mode === 'create' ? internalName : undefined,
          internalNote,
          publicId: sharePublicId || undefined,
          draft,
          preview: sanitizedPreviewForSave,
        });
      // Create History
      let historyPayload: EstimateHistoryPayloadType = {
        estimateId: response?.estimate?.id,
        userId: accountUser?.id || null,
        accountId: accountId || null,
        userLabel: !singleUser ? accountUser?.label || null : null,
        internalNote: internalNote || null,
        versionedNumber: response?.estimate?.versionedNumber || null,
        action: existingEstimate?.id ? action : 'created',
      }
      const historyRes = await createEstimateHistory(historyPayload)
      if (!historyRes) {
        toast.error('Failed to create history.')
      }
      
      const savedEstimateId = Number((response as any)?.estimate?.id || 0) || null;
      toast.success(existingEstimate ? 'Estimate updated' : 'Estimate created');
      if (action !== 'sent_and_saved') {
        trackView(MixpanelEvents.ESTIMATE_SAVE, buildEstimateActionTrackingProps());
      }
      setSaveDialogOpen(false);
      setInternalNote('');
      await finalizeAfterSave(savedEstimateId);
      if (savedEstimateId && typeof onSaved === 'function') {
        await onSaved(savedEstimateId);
      }
      if (options?.closeAfterSave !== false) {
        onClose();
      }
      return savedEstimateId;
    } catch (error: any) {
      console.error('Failed to save estimate:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to save estimate';
      if (String(message).toLowerCase().includes('already exists')) {
        setBaseNumberError('Estimate number already exists.');
      }
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    accountId,
    baseNumber,
    clientId,
    context?.client?.billingSalesTax,
    draft,
    existingEstimate,
    finalizeAfterSave,
    buildEstimateActionTrackingProps,
    internalName,
    internalNote,
    mode,
    preview,
    selectedFeeIds,
    selectedItemIds,
    sharePublicId,
    onClose,
    onSaved,
    validateBeforeSave,
  ]);

  const handleSend = useCallback(async () => {
    if (!normalizeEmailList(emailDraft.to).length) {
      toast.error('Add at least one recipient');
      return;
    }

    setSending(true);
    try {
      const senderProfile = resolveSenderProfile(accountUser);
      let targetEstimateId = existingEstimate?.id || null;
      if (sendDialogMode === 'saveAndSend') {
        const savedEstimateId = await handleSave('sent_and_saved', { closeAfterSave: false });
        if (!savedEstimateId) return;
        targetEstimateId = savedEstimateId;
      }

      if (!targetEstimateId) {
        toast.error('Estimate is missing');
        return;
      }

      await sendEstimate({
        accountId,
        estimateId: targetEstimateId,
        to: normalizeEmailList(emailDraft.to),
        cc: normalizeEmailList([
          ...emailDraft.cc,
          ...resolveAccountEstimateSendDefaults({ account, sender: senderProfile }).cc,
        ]),
        replyTo: normalizeEmailList([
          ...emailDraft.replyTo,
          ...resolveAccountEstimateSendDefaults({ account, sender: senderProfile }).replyTo,
        ]),
        subject: emailDraft.subject,
        body: emailDraft.body,
        sender: {
          firstName: senderProfile.firstName,
          lastName: senderProfile.lastName,
          email: senderProfile.email,
        },
        companyName: accountCompanyName,
        clientName: billingClientName,
        projectName,
        markAsSent: true,
      });
      toast.success('Estimate sent');
      if (sendDialogMode === 'saveAndSend') {
        trackView(MixpanelEvents.ESTIMATE_SAVE_AND_SEND, buildEstimateActionTrackingProps());
      }
      setSendDialogOpen(false);
      await finalizeAfterSave(targetEstimateId);
    } catch (error: any) {
      console.error('Failed to send estimate:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send estimate');
    } finally {
      setSending(false);
    }
  }, [
    accountId,
    accountUser?.email,
    accountUser?.firstName,
    accountUser?.lastName,
    account,
    accountCompanyName,
    billingClientName,
    buildEstimateActionTrackingProps,
    emailDraft.body,
    emailDraft.cc,
    emailDraft.replyTo,
    emailDraft.subject,
    emailDraft.to,
    existingEstimate?.id,
    finalizeAfterSave,
    handleSave,
    projectName,
    sendDialogMode,
  ]);

  const handleDelete = useCallback(async () => {
    if (!existingEstimate?.id) return;
    setDeleting(true);
    try {
      await deleteEstimate({ accountId, estimateId: existingEstimate.id });
      toast.success('Estimate deleted');
      setDeleteDialogOpen(false);
      await reloadList();
      onClose();
    } catch (error) {
      console.error('Failed to delete estimate:', error);
      toast.error('Failed to delete estimate');
    } finally {
      setDeleting(false);
    }
  }, [accountId, existingEstimate?.id, onClose, reloadList]);

  const handleExportQuickBooks = useCallback(async ({
    forceNoBundling = false,
    estimateOverride = null,
  }: {
    forceNoBundling?: boolean;
    estimateOverride?: ProjectEstimate | null;
  } = {}) => {
    const estimateToExport = estimateOverride || existingEstimate;
    const estimateBodyToExport = estimateToExport?.estimateBody || {};
    if (!estimateToExport?.id) return;
    if (!qboConnected) {
      trackView(
        MixpanelEvents.ESTIMATE_QB,
        buildEstimateActionTrackingProps({
          result: 'error',
          fe_error_text: 'Link your QuickBooks account to enable exporting.',
        })
      );
      toast.error('Link your QuickBooks account to enable exporting.');
      return;
    }
    if (mode === 'edit') {
      trackView(
        MixpanelEvents.ESTIMATE_QB,
        buildEstimateActionTrackingProps({
          result: 'error',
          fe_error_text: 'Save changes before exporting to QuickBooks.',
        })
      );
      toast.info('Save changes before exporting to QuickBooks.');
      return;
    }
    const hasSelectedItems = Array.isArray(estimateToExport.items) && estimateToExport.items.length > 0;
    const hasSelectedFees =
      Array.isArray(estimateBodyToExport?.selectedFeeIds) && estimateBodyToExport.selectedFeeIds.length > 0;
    if (!hasSelectedItems && !hasSelectedFees) {
      trackView(
        MixpanelEvents.ESTIMATE_QB,
        buildEstimateActionTrackingProps({
          result: 'error',
          fe_error_text: 'This estimate has no selected items.',
        })
      );
      toast.error('This estimate has no selected items.');
      return;
    }
    bundleSetupPendingEstimateRef.current = estimateToExport;
    setBundleSetupPendingEstimate(estimateToExport);
    setExporting(true);
    try {
      const exportRes = await exportEstimateFromBuilder({
        accountId: Number(accountId || 0),
        estimateId: estimateToExport.id,
        forceNoBundling,
      });

      if (exportRes?.error) {
        const message = exportRes.message || 'Failed to export estimate to QuickBooks';
        trackView(
          MixpanelEvents.ESTIMATE_QB,
          buildEstimateActionTrackingProps({
            result: 'error',
            be_error_text: message,
          })
        );
        if (isQuickBooksBundleSetupRequired(exportRes)) {
          setBundleSetupMissingItems(exportRes.missingBundleItems || []);
          bundleSetupPendingEstimateRef.current = estimateToExport;
          setBundleSetupPendingEstimate(estimateToExport);
          setBundleSetupModalOpen(true);
          return;
        }
        bundleSetupPendingEstimateRef.current = null;
        setBundleSetupPendingEstimate(null);
        const onRetry = () => void handleExportQuickBooks({ estimateOverride: estimateToExport });
        if (isQuickBooksOnlineError(exportRes)) {
          showQuickBooksOnlineErrorToast({
            message,
            onRetry,
            onBundleSetupRequired: () => {
              setBundleSetupMissingItems(exportRes.missingBundleItems || []);
              bundleSetupPendingEstimateRef.current = estimateToExport;
              setBundleSetupPendingEstimate(estimateToExport);
              setBundleSetupModalOpen(true);
            },
          });
        } else {
          toast.error(message, {
            duration: Infinity,
            action: {
              label: 'Retry',
              onClick: onRetry,
            },
          });
        }
        return;
      }

      const exportedEstimateId = String(
        exportRes?.estimateId || exportRes?.result?.Estimate?.Id || ''
      );
      const exportedEstimateDocNumber = String(
        exportRes?.result?.Estimate?.DocNumber || ''
      );

      const exportedAt = exportRes?.qbExportedAt || new Date().toISOString();
      const updatedEstimate = await updateEstimate({
        accountId,
        estimateId: estimateToExport.id,
        patch: {
          status: 'Exported to QB',
          qbEstimateId: exportRes?.estimateId || exportRes?.result?.Estimate?.Id || null,
          qbEstimateNumber: exportRes?.result?.Estimate?.DocNumber || null,
          qbExportedAt: exportedAt,
        },
      });
      toast.success(
        estimateToExport?.qbEstimateId
          ? 'Estimate updated in QuickBooks'
          : 'Estimate exported to QuickBooks'
      );
      // Create History
      let historyPayload: EstimateHistoryPayloadType = {
        estimateId: estimateToExport.id,
        userId: accountUser?.id || null,
        accountId: accountId || null,
        userLabel: !singleUser ? accountUser?.label || null : null,
        internalNote: internalNote || null,
        versionedNumber: updatedEstimate?.estimate?.versionedNumber || null,
        action: 'exported_to_qb',
        qbEstimateNumber: exportRes?.result?.Estimate?.DocNumber || null,
      }
      const historyRes = await createEstimateHistory(historyPayload)
      if (!historyRes) {
        toast.error('Failed to create history.')
      }

      trackView(
        MixpanelEvents.ESTIMATE_QB,
        buildEstimateActionTrackingProps({
          result: 'success',
        })
      );

      if (exportedEstimateId) {
        setExportedQbEstimateId(exportedEstimateId);
        setExportedQbEstimateDocNumber(exportedEstimateDocNumber);
        setExportSuccessModalOpen(true);
      }
      await finalizeAfterSave(estimateToExport.id);
      bundleSetupPendingEstimateRef.current = null;
      setBundleSetupPendingEstimate(null);
    } catch (error: any) {
      bundleSetupPendingEstimateRef.current = null;
      setBundleSetupPendingEstimate(null);
      console.error('Failed to export estimate to QuickBooks:', error);
      const message = resolveErrorMessage(error, 'Failed to export estimate to QuickBooks');
      trackView(
        MixpanelEvents.ESTIMATE_QB,
        buildEstimateActionTrackingProps({
          result: 'error',
          be_error_text: message,
        })
      );
      toast.error(message, {
        duration: Infinity,
        action: {
          label: 'Retry',
          onClick: () => void handleExportQuickBooks({ estimateOverride: estimateToExport }),
        },
      });
    } finally {
      setExporting(false);
    }
  }, [
    accountId,
    buildEstimateActionTrackingProps,
    existingEstimate,
    finalizeAfterSave,
    mode,
    qboConnected,
  ]);

  const handleCloseBundleSetupModal = useCallback(() => {
    setBundleSetupModalOpen(false);
    setBundleSetupMissingItems([]);
    setBundleSetupPendingEstimate(null);
    bundleSetupPendingEstimateRef.current = null;
  }, []);

  const handleRetryBundleExport = useCallback(() => {
    const pendingEstimate =
      bundleSetupPendingEstimate || bundleSetupPendingEstimateRef.current || existingEstimate;
    if (!pendingEstimate) {
      handleCloseBundleSetupModal();
      return;
    }
    handleCloseBundleSetupModal();
    void handleExportQuickBooks({ estimateOverride: pendingEstimate });
  }, [bundleSetupPendingEstimate, existingEstimate, handleCloseBundleSetupModal, handleExportQuickBooks]);

  const handleExportWithoutBundling = useCallback(() => {
    const pendingEstimate =
      bundleSetupPendingEstimate || bundleSetupPendingEstimateRef.current || existingEstimate;
    if (!pendingEstimate) {
      handleCloseBundleSetupModal();
      return;
    }
    handleCloseBundleSetupModal();
    void handleExportQuickBooks({ estimateOverride: pendingEstimate, forceNoBundling: true });
  }, [bundleSetupPendingEstimate, existingEstimate, handleCloseBundleSetupModal, handleExportQuickBooks]);

  // *** Export as PDF ***
  const handleDownloadingPDF = (val: boolean) => {
    setPdfDownloading(val);
  };

  const handleExportAsPDF = async (estimateId?: number) => {
    const id = Number(estimateId);
    
    if (!Number.isInteger(id) || id <= 0) {
      console.error('Estimate ID must be a number or its missing.');
      return;
    }

    trackView(MixpanelEvents.ESTIMATE_PDF, buildEstimateActionTrackingProps());
    handleDownloadingPDF(true);

    try {
      const response = await downloadEstimatePdf({ estimateId: id, accountId });
      if (response) {
        downloadUrl(response.downloadLink, response?.pdfName)
      }
    } catch (error: any) {
      console.error('Failed to download estimate PDF:', error);
      toast.error(error?.message || 'Failed to export estimate PDF');
    } finally {
      handleDownloadingPDF(false);
    }
  };
  // *** End of Export as PDF ***

  const handleCopyShareLink = useCallback(async () => {
    const shareUrl = buildShareUrl(existingEstimate?.publicId || sharePublicId) || existingBody?.shareUrl;
    if (!shareUrl) {
      toast.error('Share link is not available yet');
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch (error) {
      console.error('Failed to copy share link:', error);
      toast.error('Failed to copy share link');
    }
  }, [existingBody?.shareUrl, existingEstimate?.publicId, sharePublicId]);

  const handlePreview = useCallback(() => {
    const shareUrl = buildShareUrl(existingEstimate?.publicId || sharePublicId) || existingBody?.shareUrl;
    if (!shareUrl) {
      toast.error('Preview link is not available yet');
      return;
    }
    trackView(MixpanelEvents.ESTIMATE_PREVIEW, buildEstimateActionTrackingProps());
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }, [
    buildEstimateActionTrackingProps,
    existingBody?.shareUrl,
    existingEstimate?.publicId,
    sharePublicId,
  ]);

  const handleCancelEdit = useCallback(() => {
    if (!initialStateRef.current) {
      setMode('view');
      return;
    }
    const loaded = initialStateRef.current;
    setMode('view');
    setBaseNumber(loaded.baseNumber);
    setBaseNumberError('');
    setInternalName(loaded.internalName);
    setInternalNote('');
    setDraft(loaded.draft);
    setSelectedItemIds(loaded.selectedItemIds);
    setSelectedFeeIds(loaded.selectedFeeIds);
    setPreview(loaded.preview);
    setSharePublicId(loaded.sharePublicId);
    setMobileTab('preview');
  }, []);

  const handleEnterEdit = useCallback(() => {
    if (existingEstimate?.id) {
      void loadContext('edit', existingEstimate.id);
      return;
    }
    setMode('edit');
  }, [existingEstimate?.id, loadContext]);

  const footerVersionedNumber = useMemo(() => {
    const safeBaseNumber = sanitizeEstimateBaseNumber(baseNumber || context?.nextBaseNumber || '');
    const nextVersion = existingEstimate ? currentEstimateParts.version + 1 : 1;
    return buildVersionedNumber(safeBaseNumber, nextVersion);
  }, [baseNumber, context?.nextBaseNumber, currentEstimateParts.version, existingEstimate]);
  const saveDialogBaseNumber = useMemo(
    () => sanitizeEstimateBaseNumber(baseNumber || context?.nextBaseNumber || ''),
    [baseNumber, context?.nextBaseNumber]
  );
  const saveDialogVersionSummary = useMemo(() => {
    if (!saveDialogBaseNumber) return '';
    if (existingEstimate) {
      return `EST ${saveDialogBaseNumber} — Saving as ${footerVersionedNumber}`;
    }
    return `EST ${saveDialogBaseNumber} — Initial version`;
  }, [existingEstimate, footerVersionedNumber, saveDialogBaseNumber]);

  const currentTaxRate = toNonNegativeNumber(
    draft.tax.taxPercent ??
    draft.tax.clientTaxRate ??
    preview?.taxPercent ??
    preview?.clientTaxRate ??
    context?.client?.billingSalesTax,
    0
  );
  const taxableSubtotal = useMemo(() => {
    if (!preview) return 0;
    const taxableSelectionIdSet = new Set(normalizeStringArray(draft.tax.taxableLineIds));

    const roomRows = (preview.roomSections || []).flatMap((section) =>
      Array.isArray(section?.rows) ? section.rows : []
    );
    const projectFeeRows = Array.isArray(preview.projectFees) ? preview.projectFees : [];
    const allRows = [...roomRows, ...projectFeeRows];

    return allRows.reduce((sum, row) => {
      return sum + getEstimateRowTaxableAmount(row, taxableSelectionIdSet);
    }, 0);
  }, [draft.tax.taxableLineIds, preview]);
  const subtotal = useMemo(() => getEstimateSubtotal(preview), [preview]);
  const normalizedDiscount = useMemo(
    () => normalizeEstimateDiscount(draft.discount || preview?.discount || null, subtotal),
    [draft.discount, preview?.discount, subtotal]
  );
  const discountAmount = toFiniteNumber(normalizedDiscount?.amount, 0);
  const taxableSubtotalAfterDiscount = useMemo(
    () =>
      calculateDiscountedTaxableSubtotal({
        subtotal,
        taxableSubtotal,
        discount: normalizedDiscount,
      }),
    [normalizedDiscount, subtotal, taxableSubtotal]
  );
  const calculatedEstimatedTaxAmount = useMemo(() => {
    return roundCurrencyAmount(taxableSubtotalAfterDiscount * (currentTaxRate / 100));
  }, [taxableSubtotalAfterDiscount, currentTaxRate]);
  const estimatedTaxAmount = useMemo(() => {
    if (draft.tax.mode === 'excluded') return 0;
    if (draft.tax.mode === 'manual') return toNonNegativeNumber(draft.tax.manualAmount, 0);
    if (draft.tax.mode === 'calculated') return calculatedEstimatedTaxAmount;
    return toFiniteNumber(preview?.totals?.taxAmount, 0);
  }, [calculatedEstimatedTaxAmount, draft.tax.manualAmount, draft.tax.mode, preview?.totals?.taxAmount]);
  const estimatedGrandTotal = useMemo(() => {
    return roundCurrencyAmount(subtotal + discountAmount + estimatedTaxAmount);
  }, [discountAmount, estimatedTaxAmount, subtotal]);
  const canEditTax = mode === 'create' || mode === 'edit';
  const getTaxModeLabel = (value: string) => {
    if (value === 'calculated') {
      return `Calculate (${formatTaxRate(currentTaxRate)}%)`;
    }
    return TAX_MODE_OPTIONS.find((option) => option.value === value)?.label || value;
  };
  const pricingTableLikeInputBase =
    'h-10 rounded-md border border-[#dbe3ee] bg-white px-3 text-[13px] text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus-visible:border-[#8bc2db] focus-visible:ring-0 disabled:opacity-100 disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]';
  const nonStickyLastCellClassName =
    'last:static last:right-auto last:shadow-none group-data-[scrollable=true]:last:shadow-none';
  const summaryTrailingCellClassName =
    mode === 'view' || isMobile
      ? `p-0 w-0 min-w-0 max-w-0 ${nonStickyLastCellClassName}`
      : nonStickyLastCellClassName;

  const renderPreviewRows = (rows: EstimatePreviewRow[]) => {
    const orderedRows = Array.isArray(rows) ? rows : [];
    const hasClientProvidedRows = orderedRows.some((row) => row.isCom === true);
    const taxableSelectionIdSet = new Set(normalizeStringArray(draft.tax.taxableLineIds));

    return orderedRows.map((row, rowIndex) => {
      const previousRow = rowIndex > 0 ? orderedRows[rowIndex - 1] : null;
      const shouldRenderClientProvidedHeader =
        hasClientProvidedRows &&
        row.isCom === true &&
        (!previousRow || previousRow.isCom !== true);
      const isFabricationRow = row.rowType === 'fabrication';
      const isFeeRow = row.rowType === 'project-fee' || row.rowType === 'order-fee';
      const isPgBuildComponent = isPgBuildComponentRow(row);
      const isViewMode = mode === 'view';
      const rowDescriptionForDisplay = buildPreviewRowDescriptionForDisplay(
        row,
        row.descriptionShort || row.description
      );
      const rowTypeTitle = isPgBuildComponent ? '' : resolvePreviewRowTypeTitle(row);
      const suppressEmptyPartsDescription =
        !rowDescriptionForDisplay &&
        ['parts', 'supplies'].some((token) =>
          String(row?.qboType || '').toLowerCase().includes(token)
        );
      const isBundleHeaderRow = row?.isBundleParent === true;
      const showTaxableCheckbox =
        draft.tax.mode === 'calculated' && row.taxableEligible && !isBundleHeaderRow;
      const canEditTaxable = (mode === 'create' || mode === 'edit') && showTaxableCheckbox;
      const isTaxableChecked = taxableSelectionIdSet.has(String(row.id));
      const showIncludedInAmountCell = !!row.includedInPrice && row.isCom !== true;
      const rowClassName = isBundleHeaderRow
        ? 'border-b border-[#D8E2E8] bg-[#F8FAFC] hover:bg-[#F8FAFC]'
        : isViewMode
          ? row.includedInPrice || row.muted
            ? 'border-b border-[#EEF2F5] bg-[#FAFBFC] text-[#64748B] hover:bg-[#FAFBFC]'
            : 'border-b border-[#EEF2F5] bg-white hover:bg-white'
          : `border-b border-[#D8E2E8] bg-white hover:bg-white ${row.muted ? 'text-muted-foreground' : ''}`;

      const bundleHeaderDescription =
        rowDescriptionForDisplay && rowDescriptionForDisplay !== row.itemLabel
          ? rowDescriptionForDisplay
          : '';

      return (
        <React.Fragment key={`${row.id}-${rowIndex}`}>
          {shouldRenderClientProvidedHeader ? (
            <TableRow className="border-b border-[#D8E2E8] bg-white hover:bg-white">
              <TableCell className={`align-middle border-none pl-[12px] ${isViewMode ? 'w-[240px] min-w-[240px] max-w-[240px]' : 'w-[140px]'}`} />
              <TableCell colSpan={4} className="align-middle border-none py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#5B6B83]">
                CLIENT-PROVIDED
              </TableCell>
              <TableCell className={`w-[136px] min-w-[136px] max-w-[136px] border-none last:w-[136px] last:min-w-[136px] last:max-w-[136px] ${nonStickyLastCellClassName}`} />
            </TableRow>
          ) : null}
          <TableRow className={rowClassName}>
          <TableCell
            className={`align-top border-none pl-[12px] ${
              isViewMode
                ? 'w-[240px] min-w-[240px] max-w-[240px] whitespace-normal break-words'
                : 'w-[140px] min-w-[140px] max-w-[140px] whitespace-normal break-words'
            }`}
          >
            {row.rowType === 'item' ? (
              <div className="flex items-start gap-3">
                {row.photoId ? <PreviewPhoto avatarId={row.photoId} /> : null}
                <div
                  className={
                    isViewMode
                      ? 'block break-words whitespace-normal text-[14px] font-semibold leading-[1.2] text-[rgb(59,151,177)]'
                      : 'block break-words whitespace-normal font-semibold text-[rgb(59,151,177)]'
                  }
                >
                  {row.itemLabel || row.description}
                </div>
              </div>
            ) : isPgBuildComponent ? (
              <div className="block break-words whitespace-normal font-semibold text-[rgb(59,151,177)]">
                {buildPgBuildComponentHeading(row)}
              </div>
            ) : (isFeeRow || isFabricationRow) ? (
              <div className="h-[56px] w-[56px]" />
            ) : row.photoId ? (
              <PreviewPhoto avatarId={row.photoId} />
            ) : (
              <div className="h-[56px] w-[56px]" />
            )}
          </TableCell>
          <TableCell className={`align-top whitespace-pre-wrap border-none break-words w-[320px] min-w-[320px] ${row.rowType === 'item' ? 'text-foreground' : ''}`}>
            {isBundleHeaderRow ? (
              bundleHeaderDescription ? (
                <span className="text-sm font-medium text-[#4B5563]">{bundleHeaderDescription}</span>
              ) : null
            ) : row.rowType === 'item' ? (
              rowDescriptionForDisplay
            ) : isFeeRow ? (
              <div>
                <div className="font-semibold text-sky-700">
                  {row.itemLabel || row.description}
                </div>
                {isFeeRow && row.description ? (
                  <div className="mt-1 text-sm text-muted-foreground">{row.description}</div>
                ) : null}
              </div>
            ) : (
              <div>
                {rowTypeTitle ? (
                  <div className="mb-1 text-xs font-semibold tracking-wide text-[#64748B]">
                    {rowTypeTitle}
                  </div>
                ) : null}
                {suppressEmptyPartsDescription ? null : <div>{row.description || '—'}</div>}
              </div>
            )}
          </TableCell>
          <TableCell className="align-top border-none text-right w-[124px] min-w-[124px]">
            {formatEstimateRowQuantity(row)}
          </TableCell>
          <TableCell className="align-top border-none text-right w-[168px] min-w-[168px]">
            {formatEstimateRowUnitPrice(row)}
          </TableCell>
          <TableCell className="align-top border-none text-right w-[168px] min-w-[168px] font-medium">
            <div className={showIncludedInAmountCell ? 'text-right text-[14px] text-[#6B7280]' : 'text-right'}>{formatEstimateRowAmount(row)}</div>
            {showIncludedInAmountCell ? (
              <div className="mt-1 text-right text-[11px] italic font-medium text-[#94A3B8]">Included</div>
            ) : null}
          </TableCell>
          <TableCell
            className={`align-top last:align-top border-none text-right ${
              isViewMode
                ? 'w-[136px] min-w-[136px] max-w-[136px] last:w-[136px] last:min-w-[136px] last:max-w-[136px]'
                : 'w-[136px] min-w-[136px] last:w-[136px] last:min-w-[136px] last:max-w-[136px]'
            } ${nonStickyLastCellClassName}`}
          >
            <div className="flex w-full items-start justify-end pr-[12px]">
              {showTaxableCheckbox ? (
                isViewMode ? (
                  isTaxableChecked ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )
                ) : (
                <Checkbox
                  checked={isTaxableChecked}
                  disabled={!canEditTaxable}
                  onCheckedChange={(checked) => {
                    setTaxSelectionTouched(true);
                    const rowTaxSelectionKey = buildTaxableSelectionKey(row);
                    if (rowTaxSelectionKey) {
                      taxSelectionOverrideByKeyRef.current[rowTaxSelectionKey] = !!checked;
                    }
                    const nextId = String(row.id);
                    const nextSet = new Set(draft.tax.taxableLineIds || []);
                    if (checked) {
                      nextSet.add(nextId);
                    } else {
                      nextSet.delete(nextId);
                    }
                    setDraftTax({ taxableLineIds: [...nextSet] });
                  }}
                />
                )
              ) : draft.tax.mode === 'calculated' &&
                row.taxableEligible &&
                !isBundleHeaderRow &&
                isTaxableChecked ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </TableCell>
        </TableRow>
        </React.Fragment>
      );
    });
  };

  const eligibleSelectionState = useMemo(() => {
    const allItemIds = (context?.availableItems || [])
      .filter((item) => shouldRenderAvailableItemInSelector(item, selectedItemIds))
      .filter((item) => item.isCalculated)
      .map((item) => Number(item.id));
    const allFeeIds = (context?.availableFees || [])
      .map((fee) => Number(fee.id));

    const totalEligibleCount = allItemIds.length + allFeeIds.length;
    if (totalEligibleCount === 0) return { allSelected: false, someSelected: false };

    const selectedEligibleCount =
      allItemIds.filter(id => selectedItemIds.includes(id)).length +
      allFeeIds.filter(id => selectedFeeIds.includes(id)).length;

    return {
      allSelected: selectedEligibleCount === totalEligibleCount,
      someSelected: selectedEligibleCount > 0 && selectedEligibleCount < totalEligibleCount
    };
  }, [context?.availableFees, context?.availableItems, selectedFeeIds, selectedItemIds]);

  const selectorPanel = shouldRenderBuilderBody ? (
    <div className={`flex flex-col border-r border-input bg-white ${mode !== 'view' && !isMobile ? '' : 'h-full min-h-0'}`}>
      <div className="border-b border-input px-4 pt-4 pb-2">
        <div className="mb-4">
          <div className="text-[20px] font-semibold leading-tight">Select Items</div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={eligibleSelectionState.allSelected}
              minusIcon={eligibleSelectionState.someSelected}
              onCheckedChange={(checked) => {
                if (checked) handleSelectAll();
                else handleDeselectAll();
              }}
              disabled={mode === 'view'}
            />
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              ITEMS & FEES
            </span>
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            ESTIMATED
          </span>
        </div>
      </div>
      <div className={`${mode !== 'view' && !isMobile ? '' : 'flex-1 overflow-y-auto'} px-4 py-4`}>
        <div className="space-y-5">
          {groupedItems.map(({ roomName, items }) => {
            const eligibleItemIds = items.filter((item) => item.isCalculated).map((item) => item.id);
            const selectedInRoom = eligibleItemIds.filter((itemId) => selectedItemIds.includes(itemId)).length;
            const allSelected = eligibleItemIds.length > 0 && selectedInRoom === eligibleItemIds.length;
            return (
              <div key={roomName} className="space-y-2">
                <div className="flex items-center justify-between gap-3 pb-2">
                  <label className="inline-flex items-center gap-2 font-semibold text-foreground">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(checked) => handleRoomToggle(items, !!checked)}
                      minusIcon={selectedInRoom > 0 && !allSelected}
                      disabled={!eligibleItemIds.length || mode === 'view'}
                    />
                    {roomName}
                  </label>
                  <span className="text-sm text-muted-foreground">{selectedInRoom}/{eligibleItemIds.length}</span>
                </div>
                <div className="space-y-1">
                  {items.map((item) => {
                    const itemDisplayLabel = item.archived
                      ? `${item.itemLabel} (archived)`
                      : item.itemLabel;
                    const otherEstimateLabels = estimateUsageByItem.get(item.id) || [];
                    const isItemDisabled = !item.isCalculated;
                    const primaryEstimateLabel = otherEstimateLabels[0] || '';
                    const additionalEstimateLabels = otherEstimateLabels.slice(1);
                    const tagsNode = (
                      <div className="flex max-w-[145px] flex-wrap items-center justify-end gap-1">
                        {primaryEstimateLabel ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold leading-none text-emerald-700">
                            {primaryEstimateLabel}
                          </span>
                        ) : null}
                        <EstimateSelectorItemStatusTag status={item.status} />
                      </div>
                    );
                    const estimateTagsNode =
                      mode === 'create' && additionalEstimateLabels.length > 0 ? (
                        <div className="mt-1 flex items-center justify-end gap-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold leading-none text-blue-700 hover:bg-blue-200"
                              >
                                +{additionalEstimateLabels.length}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-[180px] p-3">
                              <div className="space-y-1">
                                <div className="text-sm font-semibold text-muted-foreground">Also in:</div>
                                {additionalEstimateLabels.map((label) => (
                                  <div key={label} className="text-sm text-foreground">{label}</div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      ) : null;
                    const itemCard = (
                      <div
                        key={item.id}
                        className={`rounded-md border px-3 py-2 ${
                          isItemDisabled
                            ? 'border-input/70 bg-muted/15 opacity-60'
                            : selectedItemIds.includes(item.id)
                              ? 'border-primary/30 bg-primary/5'
                              : 'border-transparent bg-muted/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            disabled={!item.isCalculated || mode === 'view'}
                            onCheckedChange={(checked) => {
                              setSelectedItemIds((prev) => {
                                if (checked) {
                                  return [...new Set([...prev, item.id])];
                                }
                                return prev.filter((value) => value !== item.id);
                              });
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                {isItemDisabled ? (
                                  <>
                                    <span className="block min-w-0 overflow-hidden text-ellipsis line-clamp-2 whitespace-normal text-[15px] leading-5 text-muted-foreground/80">
                                      {itemDisplayLabel}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <TruncatedTooltipText
                                      text={itemDisplayLabel}
                                      lines={2}
                                      className="text-[15px] leading-5 text-foreground"
                                      tooltipClassName="z-[99999999]"
                                    />
                                  </>
                                )}
                              </div>
                              <div className="flex shrink-0 flex-col items-end">
                                {tagsNode}
                                {estimateTagsNode}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                    if (!isItemDisabled) {
                      return itemCard;
                    }

                    return (
                      <Tooltip key={item.id}>
                        <TooltipTrigger asChild>
                          <div className="cursor-not-allowed">{itemCard}</div>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6} className="z-[99999999]">
                          {PRICING_NOT_CALCULATED_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {context?.availableFees?.length ? (
            <div className="space-y-2 border-t border-input pt-4">
              <div className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">Miscellaneous</div>
              <div className="space-y-1">
                {context.availableFees.map((fee) => {
                  const otherEstimateLabels = estimateUsageByFee.get(fee.id) || [];
                  const primaryEstimateLabel = otherEstimateLabels[0] || '';
                  const additionalEstimateLabels = otherEstimateLabels.slice(1);
                  const amountNode = primaryEstimateLabel ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold leading-none text-emerald-700">
                      {primaryEstimateLabel}
                    </span>
                  ) : (
                    <div className="text-sm font-medium text-muted-foreground/80">{' '}</div>
                  );
                  const estimateTagsNode =
                    mode === 'create' && additionalEstimateLabels.length > 0 ? (
                      <div className="mt-1 flex items-center justify-end gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold leading-none text-blue-700 hover:bg-blue-200"
                            >
                              +{additionalEstimateLabels.length}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-[180px] p-3">
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-muted-foreground">Also in:</div>
                              {additionalEstimateLabels.map((label) => (
                                <div key={label} className="text-sm text-foreground">{label}</div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ) : null;
                  return (
                    <label
                      key={fee.id}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 ${selectedFeeIds.includes(fee.id) ? 'border-primary/30 bg-primary/5' : 'border-transparent bg-muted/30'}`}
                    >
                      <Checkbox
                        checked={selectedFeeIds.includes(fee.id)}
                        disabled={mode === 'view'}
                        onCheckedChange={(checked) => {
                          setSelectedFeeIds((prev) => {
                            if (checked) {
                              return [...new Set([...prev, fee.id])];
                            }
                            return prev.filter((value) => value !== fee.id);
                          });
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <TruncatedTooltipText
                          text={fee.itemLabel || fee.description || 'Project Fee'}
                          className="text-[15px] text-foreground"
                        />
                      </div>
                      <div className="flex shrink-0 flex-col items-end">
                        {amountNode}
                        {estimateTagsNode}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;

  const previewPanel = shouldRenderBuilderBody ? (
    <div className={`flex min-h-0 flex-col bg-white ${mode !== 'view' && !isMobile ? '' : mode === 'view' ? '' : 'h-full'}`}>
      <div className="border-b border-input px-4 py-4 md:px-6">
        <div className="text-[22px] font-semibold leading-tight">{projectName}</div>
      </div>
      <div className={`${mode === 'view' || (mode !== 'view' && !isMobile) ? '' : 'min-h-0 flex-1 overflow-y-auto'} px-4 py-4 md:px-6`}>
        {previewLoading && !preview ? (
          <div className="flex min-h-[240px] items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading preview...
          </div>
        ) : null}

        {!previewLoading && !preview ? (
            <div className="flex flex-col min-h-[240px] items-center justify-center rounded-md border border-dashed border-input text-muted-foreground">
              <p className="font-bold">No items selected.</p>
              <p>Select items and fees from the left panel to build your estimate.</p>
            </div>
        ) : null}

        {preview ? (
          <div className="overflow-x-auto overflow-y-visible bg-white">
            <Table
              className={`table-fixed ${isMobile ? 'min-w-[900px]' : ''}`}
              wrapperClassName="w-full max-w-none rounded-none border-0 max-h-none md:max-w-none md:max-h-none overflow-x-auto overflow-y-visible"
            >
              <TableHeader>
                <TableRow className={mode === 'view' ? 'border-b border-[#D8E2E8] bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]' : 'border-b-2 border-muted/50 bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]'}>
                  <TableHead className={`pl-[12px] h-10 text-[11px] font-bold tracking-wider text-left ${mode === 'view' ? 'w-[240px] min-w-[240px] max-w-[240px] text-[#5B6B83]' : 'w-[140px]'}`}>Item / Photo</TableHead>
                  <TableHead className={`pl-[12px] h-10 w-[320px] min-w-[320px] text-[11px] font-bold tracking-wider text-left ${mode === 'view' ? 'text-[#5B6B83]' : ''}`}>Description</TableHead>
                  <TableHead className={`w-[124px] min-w-[124px] pr-[12px] text-right h-10 text-[11px] font-bold tracking-wider ${mode === 'view' ? 'text-[#5B6B83]' : ''}`}>Qty</TableHead>
                  <TableHead className={`w-[168px] min-w-[168px] pr-[12px] text-right h-10 text-[11px] font-bold tracking-wider ${mode === 'view' ? 'text-[#5B6B83]' : ''}`}>Rate</TableHead>
                  <TableHead className={`w-[168px] min-w-[168px] pr-[12px] text-right h-10 text-[11px] font-bold tracking-wider ${mode === 'view' ? 'text-[#5B6B83]' : ''}`}>Amount</TableHead>
                  <TableHead className={`pr-[12px] text-right h-10 text-[11px] font-bold tracking-wider ${mode === 'view' ? 'w-[136px] min-w-[136px] max-w-[136px] last:w-[136px] last:min-w-[136px] last:max-w-[136px] text-[#5B6B83]' : 'w-[136px] min-w-[136px] last:w-[136px] last:min-w-[136px] last:max-w-[136px]'} ${nonStickyLastCellClassName}`}>Taxable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.roomSections.map((section) => (
                  <React.Fragment key={section.roomName}>
                    <TableRow className={mode === 'view' ? 'border-b border-[#D8E2E8] bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]' : 'border-none bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]'}>
                      <TableCell colSpan={4} className="font-bold py-2 text-foreground text-[14px]">{section.roomName}</TableCell>
                      <TableCell className="text-right py-2 font-bold text-[14px]">{formatCurrency(section.subtotal)}</TableCell>
                      <TableCell className={nonStickyLastCellClassName} />
                    </TableRow>
                    {renderPreviewRows(section.rows)}
                  </React.Fragment>
                ))}

                {preview.projectFees.length ? (
                  <>
                    <TableRow className={mode === 'view' ? 'border-b border-[#D8E2E8] bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]' : 'border-none bg-[hsl(210_40%_96.1%)] hover:bg-[hsl(210_40%_96.1%)]'}>
                      <TableCell className="font-bold py-2 text-foreground text-[14px]" colSpan={4}>Miscellaneous</TableCell>
                      <TableCell className="text-right py-2 font-bold text-[14px]">{formatCurrency(preview.totals.feesSubtotal)}</TableCell>
                      <TableCell className={nonStickyLastCellClassName} />
                    </TableRow>
                    {renderPreviewRows(preview.projectFees)}
                  </>
                ) : null}

                <TableRow className="bg-white border-t-2 border-muted/40 hover:bg-transparent">
                  <TableCell colSpan={mode === 'view' ? 2 : 3} />
                  <TableCell colSpan={mode === 'view' ? 2 : 1} className="text-right text-base font-semibold text-foreground pt-6 whitespace-normal">Subtotal</TableCell>
                  <TableCell className="text-right text-base font-semibold text-foreground pt-6">
                    {formatCurrency(subtotal)}
                  </TableCell>
                  <TableCell className={summaryTrailingCellClassName} />
                </TableRow>
                {normalizedDiscount?.enabled ? (
                  <TableRow className="bg-white border-none hover:bg-transparent">
                    <TableCell colSpan={2} />
                    <TableCell colSpan={2} className="text-right text-base font-semibold text-foreground whitespace-normal">
                      {canEditTax ? (
                        <Input
                          className={`${pricingTableLikeInputBase} ml-auto w-full max-w-[420px] text-right`}
                          value={draft.discount?.label || DISCOUNT_DEFAULT_LABEL}
                          placeholder={DISCOUNT_DEFAULT_LABEL}
                          onChange={(event) => setDraftDiscount({ label: event.target.value })}
                          onBlur={(event) => {
                            const nextLabel = event.target.value.trim() || DISCOUNT_DEFAULT_LABEL;
                            setDraftDiscount({ label: nextLabel });
                          }}
                        />
                      ) : (
                        <span className="text-muted-foreground font-medium">
                          {normalizedDiscount.label || DISCOUNT_DEFAULT_LABEL}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-base font-semibold text-foreground">
                      {canEditTax ? (
                        <div className="ml-auto flex w-fit items-center justify-end gap-2">
                          <div className="relative w-[150px]">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="text"
                                  className="absolute left-0 top-1/2 z-10 flex h-8 min-h-8 w-[54px] -translate-y-1/2 items-center justify-between rounded-none border-none bg-transparent px-2 py-1 text-sm focus:ring-0 focus:ring-offset-0"
                                >
                                  <span>{normalizeDiscountType(draft.discount?.type) === 'amount' ? '$' : '%'}</span>
                                  <ChevronDown className="h-4 w-4 opacity-50" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="z-[99999990]" align="start">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setDiscountInputValue(null);
                                    setDraftDiscount({ type: 'amount' });
                                  }}
                                >
                                  $
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setDiscountInputValue(null);
                                    setDraftDiscount({ type: 'percent', value: '', amount: 0 });
                                  }}
                                >
                                  %
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <span className="pointer-events-none absolute left-[74px] top-1/2 z-10 -translate-y-1/2 text-[13px] text-[#667085]">
                              -
                            </span>
                            <Input
                              inputMode="decimal"
                              className={`${pricingTableLikeInputBase} w-[150px] pl-[88px] text-right`}
                              value={discountInputValue ?? getDiscountInputValue(draft.discount)}
                              placeholder="0.00"
                              onFocus={() => {
                                setDiscountInputValue(getDiscountInputValue(draft.discount));
                              }}
                              onChange={(event) => {
                                const nextRawValue = event.target.value;
                                if (nextRawValue && !DECIMAL_INPUT_REGEX.test(nextRawValue)) return;
                                setDiscountInputValue(nextRawValue);
                                if (!nextRawValue || nextRawValue === '.') {
                                  setDraftDiscount({ value: '', amount: 0 });
                                  return;
                                }
                                const nextAbsoluteValue = toFiniteNumber(nextRawValue, 0);
                                const cappedValue =
                                  normalizeDiscountType(draft.discount?.type) === 'percent'
                                    ? Math.min(100, nextAbsoluteValue)
                                    : Math.min(Math.max(0, subtotal), nextAbsoluteValue);
                                setDraftDiscount({
                                  value: -Math.abs(cappedValue),
                                });
                              }}
                              onBlur={() => {
                                const value = discountInputValue ?? getDiscountInputValue(draft.discount);
                                if (!value || value === '.') {
                                  setDraftDiscount({ value: '', amount: 0 });
                                  setDiscountInputValue(null);
                                  return;
                                }
                                const nextAbsoluteValue = Math.abs(toFiniteNumber(value, 0));
                                const cappedValue =
                                  normalizeDiscountType(draft.discount?.type) === 'percent'
                                    ? Math.min(100, nextAbsoluteValue)
                                    : Math.min(Math.max(0, subtotal), nextAbsoluteValue);
                                setDraftDiscount({
                                  value: -Math.abs(cappedValue),
                                });
                                setDiscountInputValue(null);
                              }}
                            />
                          </div>
                          <Button type="button" variant="trash-button" onClick={handleRemoveDiscount}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-[14px]">
                          {formatCurrency(discountAmount)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={summaryTrailingCellClassName} />
                  </TableRow>
                ) : canEditTax ? (
                  <TableRow className="bg-white border-none hover:bg-transparent">
                    <TableCell colSpan={mode === 'view' ? 2 : 3} />
                    <TableCell colSpan={mode === 'view' ? 2 : 1} />
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="link"
                        className="ml-auto min-h-[32px] px-2 py-0 font-medium hover:bg-accent hover:no-underline"
                        onClick={handleAddDiscount}
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add discount</span>
                      </Button>
                    </TableCell>
                    <TableCell className={summaryTrailingCellClassName} />
                  </TableRow>
                ) : null}
                {draft.tax.mode === 'calculated' ? (
                  <TableRow className="bg-white border-none hover:bg-transparent">
                    <TableCell colSpan={mode === 'view' ? 2 : 3} />
                    <TableCell colSpan={mode === 'view' ? 2 : 1} className="text-right text-base font-semibold text-foreground whitespace-normal">
                      <span className="text-muted-foreground font-medium">Taxable subtotal</span>
                    </TableCell>
                    <TableCell className="text-right text-base font-semibold text-foreground">
                      <span className="text-muted-foreground text-[14px]">
                        {formatCurrency(taxableSubtotalAfterDiscount)}
                      </span>
                    </TableCell>
                    <TableCell className={summaryTrailingCellClassName} />
                  </TableRow>
                ) : null}

                <TableRow className="bg-white border-none hover:bg-transparent">
                  <TableCell colSpan={mode === 'view' ? 2 : 3} className="pr-4">
                    <div className="flex w-full justify-end">
                      <div className="flex w-full max-w-full flex-col items-end gap-2 sm:w-auto sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-[12px] font-medium text-sky-700 hover:text-sky-800 underline underline-offset-2"
                            >
                              <Info className="h-3.5 w-3.5" />
                              Tax Disclaimer
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            portal={false}
                            align="start"
                            side="top"
                            collisionPadding={16}
                            className="scrollbar-hidden w-[520px] max-h-[360px] max-w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto p-0 z-[999999]"
                          >
                            <div className="sticky top-0 z-10 border-b border-input bg-popover px-4 py-3">
                              <div className="text-lg font-semibold text-foreground">{TAX_DISCLAIMER_TITLE}</div>
                            </div>
                            <div className="space-y-4 px-4 py-3">
                              {TAX_DISCLAIMER_SECTIONS.map((section) => (
                                <div key={section.title} className="space-y-1">
                                  <div className="text-sm font-semibold text-foreground">{section.title}</div>
                                  <p className="max-w-full whitespace-normal break-words text-sm leading-6 text-muted-foreground">
                                    {section.body}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>

                        {canEditTax ? (
                          <select
                            className="h-8 max-w-full rounded-md border border-input bg-white px-2 text-[13px] font-medium outline-none focus:ring-1 focus:ring-primary"
                            value={draft.tax.mode}
                            onChange={(event) => {
                              const nextMode = event.target.value;
                              const fallbackTax = context?.client?.billingSalesTax || 0;
                              const nextTaxRate = currentTaxRate || fallbackTax;

                              setDraftTax({
                                mode: nextMode,
                                taxableLineIds: draft.tax.taxableLineIds || preview?.taxableLineIds || [],
                                taxPercent: nextMode === 'calculated' ? nextTaxRate : null,
                                clientTaxRate: nextMode === 'calculated' ? nextTaxRate : draft.tax.clientTaxRate
                              });
                            }}
                          >
                            {TAX_MODE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {getTaxModeLabel(option.value)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {draft.tax.mode === 'excluded'
                              ? 'Excluded'
                              : draft.tax.mode === 'manual'
                                ? 'Manual tax'
                                : getTaxModeLabel('calculated')}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell colSpan={mode === 'view' ? 2 : 1} className="text-right text-base font-semibold text-foreground">
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      <span className="text-muted-foreground font-medium">Estimated Tax</span>
                      {draft.tax.mode === 'calculated' ? (
                        canEditTax ? (
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${pricingTableLikeInputBase} w-[92px] pr-7 text-right`}
                              value={taxRateInputValue ?? String(currentTaxRate)}
                              onFocus={() => {
                                setTaxRateInputValue(String(currentTaxRate));
                              }}
                              onChange={(event) => {
                                const nextRawValue = event.target.value;
                                setTaxRateInputValue(nextRawValue);
                                if (nextRawValue === '') return;
                                const nextTaxRate = Number(nextRawValue);
                                if (!Number.isFinite(nextTaxRate)) return;
                                setDraftTax({
                                  clientTaxRate: toNonNegativeNumber(nextTaxRate, 0),
                                  taxPercent: toNonNegativeNumber(nextTaxRate, 0),
                                });
                              }}
                              onBlur={() => {
                                const valueToCommit = taxRateInputValue;
                                let nextTaxRateToPersist = currentTaxRate;
                                if (valueToCommit === null) return;
                                if (valueToCommit === '') {
                                  setDraftTax({
                                    clientTaxRate: 0,
                                    taxPercent: 0,
                                  });
                                  nextTaxRateToPersist = 0;
                                } else {
                                  const parsedValue = Number(valueToCommit);
                                  if (Number.isFinite(parsedValue)) {
                                    const nextNonNegativeTaxRate = toNonNegativeNumber(
                                      parsedValue,
                                      0
                                    );
                                    setDraftTax({
                                      clientTaxRate: nextNonNegativeTaxRate,
                                      taxPercent: nextNonNegativeTaxRate,
                                    });
                                    nextTaxRateToPersist = nextNonNegativeTaxRate;
                                  }
                                }
                                setTaxRateInputValue(null);
                                void handlePersistClientTaxRate(nextTaxRateToPersist);
                              }}
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
                              %
                            </span>
                          </div>
                        ) : (
                          <span className="text-[13px]">({formatTaxRate(preview.taxPercent ?? preview.clientTaxRate)}%)</span>
                        )
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-base font-semibold text-foreground">
                    {draft.tax.mode === 'excluded' ? (
                      <span className="text-muted-foreground text-[14px]">Excluded</span>
                    ) : draft.tax.mode === 'manual' && canEditTax ? (
                      <div className="relative ml-auto w-fit">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
                          $
                        </span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className={`${pricingTableLikeInputBase} w-[120px] pl-6 text-right`}
                          value={manualTaxInputValue ?? String(draft.tax.manualAmount)}
                          onFocus={() => {
                            setManualTaxInputValue(String(draft.tax.manualAmount));
                          }}
                          onChange={(event) => {
                            const nextRawValue = event.target.value;
                            setManualTaxInputValue(nextRawValue);
                            if (nextRawValue === '') return;
                            const nextManualAmount = Number(nextRawValue);
                            if (!Number.isFinite(nextManualAmount)) return;
                            setDraftTax({ manualAmount: toNonNegativeNumber(nextManualAmount, 0) });
                          }}
                          onBlur={() => {
                            const valueToCommit = manualTaxInputValue;
                            if (valueToCommit === null) return;
                            if (valueToCommit === '') {
                              setDraftTax({ manualAmount: 0 });
                            } else {
                              const parsedValue = Number(valueToCommit);
                              if (Number.isFinite(parsedValue)) {
                                setDraftTax({ manualAmount: toNonNegativeNumber(parsedValue, 0) });
                              }
                            }
                            setManualTaxInputValue(null);
                          }}
                        />
                      </div>
                    ) : (
                      formatCurrency(estimatedTaxAmount)
                    )}
                  </TableCell>
                  <TableCell className={summaryTrailingCellClassName} />
                </TableRow>

                <TableRow className="bg-white border-none hover:bg-transparent">
                  <TableCell colSpan={mode === 'view' ? 2 : 3} />
                  <TableCell colSpan={mode === 'view' ? 2 : 1} className="text-right text-[22px] font-bold text-foreground py-6 whitespace-normal leading-tight">Estimated Total</TableCell>
                  <TableCell className="text-right text-[22px] font-bold text-foreground py-6">{formatCurrency(estimatedGrandTotal)}</TableCell>
                  <TableCell className={summaryTrailingCellClassName} />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <>
      <Sheet
        open={open}
        modal={!clientEditModalOpen}
        onOpenChange={(nextOpen) => !nextOpen && handleClose()}
      >
        <SheetContent
          side="bottom"
          className="h-full w-full max-w-none p-0 sm:max-w-none"
          showCloseButton={false}
          data-qa="estimate-builder-sheet"
          onInteractOutside={(event) => {
            if (clientEditModalOpen || isBundleSetupModalEvent(event)) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (clientEditModalOpen || isBundleSetupModalEvent(event)) {
              event.preventDefault();
            }
          }}
          onFocusOutside={(event) => {
            if (clientEditModalOpen || isBundleSetupModalEvent(event)) {
              event.preventDefault();
            }
          }}
        >
          <div className="flex h-full flex-col bg-white">
            <div className="shrink-0 border-b border-input px-4 py-4 md:px-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <SheetTitle className="text-[22px] font-semibold leading-tight text-foreground">
                    {mode === 'create' ? (
                      'New Estimate'
                    ) : (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="shrink-0">{`Estimate ${currentEstimateParts.versionedNumber}`}</span>
                        {internalName ? (
                          <>
                            <span className="shrink-0 text-muted-foreground">-</span>
                            <TruncatedTooltipText
                              text={internalName}
                              className="max-w-[260px] text-[22px] font-semibold leading-tight text-foreground md:max-w-[420px]"
                              tooltipClassName="max-w-[520px]"
                            />
                          </>
                        ) : null}
                      </span>
                    )}
                  </SheetTitle>
                  <SheetDescription className="mt-1 text-sm text-muted-foreground">
                    {mode === 'create' ? `EST ${context?.nextBaseNumber || ''}` : null}
                  </SheetDescription>
                  {mode !== 'create' ? (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <div className="inline-flex min-h-[28px] items-center gap-2 rounded-md border border-input bg-white px-3 text-[12px]">
                        <span className="font-semibold text-muted-foreground">Status</span>
                        <span className={`font-semibold ${resolveEstimateHeaderStatusClassName(existingEstimate?.status)}`}>
                          {existingEstimate?.status || 'Created'}
                        </span>
                      </div>
                      {existingEstimate?.qbEstimateId && existingEstimate?.qbEstimateNumber ? (
                        <div className="inline-flex items-center gap-2">
                          <a
                            href={buildQboEstimateUrl(existingEstimate.qbEstimateId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-[#3B97B1] hover:underline"
                          >
                            <QuickBook width={14} height={14} />
                            #{existingEstimate.qbEstimateNumber}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {formatShortDate(resolveQbExportedAt(existingEstimate)) ? (
                            <span className="text-sm font-medium text-muted-foreground">
                              {formatShortDate(resolveQbExportedAt(existingEstimate))}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {!existingEstimate?.qbEstimateId && formatShortDate(existingEstimate?.createdAt) ? (
                        <span className="text-sm font-medium text-muted-foreground">
                          {formatShortDate(existingEstimate.createdAt)}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {mode !== 'create' ? (
                    <>
                      <Button
                        data-qa="estimate-builder-preview-trigger"
                        className="min-h-[32px] max-h-[32px] font-medium py-0 px-[12px] text-[12px]"
                        variant="outline"
                        type="button"
                        onClick={handlePreview}
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="min-h-[32px] max-h-[32px] font-medium py-0 px-[12px] text-[12px]"
                            variant="outline"
                            type="button"
                            disabled={exporting || pdfDownloading}
                            data-qa="estimate-builder-export-menu-trigger"
                          >
                            {exporting || pdfDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            Export
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[99999991] w-[220px]">
                          <DropdownMenuItem onSelect={() => void handleCopyShareLink()}>
                            <span className="inline-flex items-center gap-2">
                              <Link2 className="h-4 w-4" />
                              Create a sharable link
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-qa="estimate-builder-export-qbo-trigger"
                            onSelect={(event) => {
                              if (!qboConnected) {
                                event.preventDefault();
                                return;
                              }
                              void handleExportQuickBooks();
                            }}
                            className={!qboConnected ? 'opacity-60 cursor-default' : ''}
                          >
                            <span className="inline-flex w-full items-center gap-2">
                              <QuickBook width={14} height={14} />
                              {qboConnected
                                ? existingEstimate?.qbEstimateId
                                  ? 'Update in QuickBooks'
                                  : 'Export to QuickBooks'
                                : 'Connect to QuickBooks'}
                              {!qboConnected && <QuickBooksConnectTooltip />}
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled={pdfDownloading} onSelect={() => void handleExportAsPDF(estimateId || context?.existingEstimate?.id)}>
                            <span className="inline-flex items-center gap-2">
                              <LucideFileText width={14} height={14} />
                              Export as PDF
                            </span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <EstimateHistory singleUser={singleUser} estimateId={estimateId || context?.existingEstimate?.id} />
                    </>
                  ) : null}

                  <Button variant="ghost" type="button" size="icon" onClick={handleClose}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              {builderLoading ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Loading estimate builder...
                </div>
              ) : (
                <div className="flex h-full flex-col overflow-hidden">
                  <div className="min-h-0 overflow-y-auto">
                    <div className="border-b border-input px-4 py-6 md:px-8">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.1fr] gap-10 items-start">

                        {/* Columns 1-2: Customer, Email, Client, and Project Location */}
                        <div className="md:col-span-2 space-y-6">
                          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                            <div className="flex items-baseline gap-3">
                              <span className="text-[12px] font-bold text-muted-foreground w-20 shrink-0">Customer:</span>
                              <span className="text-[18px] text-foreground font-semibold truncate">{clientDisplayName}</span>
                            </div>
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="text-[12px] font-bold text-muted-foreground w-16 shrink-0">Email(s):</span>
                              {mode === 'view' ? (
                                <span className="text-[15px] text-foreground font-medium truncate">{draft.clientEmail || '—'}</span>
                              ) : (
                                <Input
                                  value={draft.clientEmail}
                                  className="h-8 w-[480px] max-w-[min(62vw,100%)] text-[14px]"
                                  onChange={(event) => setDraftField('clientEmail', event.target.value)}
                                  onBlur={() => void handlePersistClientEmail()}
                                  placeholder="client@example.com"
                                />
                              )}
                              {emailSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
                            <div className="space-y-1">
                              <div className={`font-bold tracking-wider text-muted-foreground mb-1 ${mode === 'view' ? 'text-[14px] uppercase' : 'text-[11px] uppercase'}`}>Client</div>
                              <div className="space-y-0.5 text-[14px] text-muted-foreground leading-relaxed">
                                {estimateClientInfo.name ? (
                                  <div className="font-bold text-foreground">{estimateClientInfo.name}</div>
                                ) : null}
                                {(estimateClientInfo.addressLines || []).map((line: string, index: number) => (
                                  <div
                                    key={line}
                                    className={!estimateClientInfo.name && index === 0 ? 'font-bold text-foreground' : undefined}
                                  >
                                    {line}
                                  </div>
                                ))}
                                {mode !== 'view' && (
                                  <button
                                    type="button"
                                    className="text-sm text-[rgb(59,151,176)] hover:text-[rgb(59,151,176)] font-bold underline block mt-2"
                                    onClick={() => void openClientEditModal(1, estimateBillingClient)}
                                    disabled={clientEditLoading}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1">
                              <div className={`font-bold tracking-wider text-muted-foreground mb-1 ${mode === 'view' ? 'text-[14px] uppercase' : 'text-[11px] uppercase'}`}>Project Location</div>
                              <div className="space-y-0.5 text-[14px] text-muted-foreground leading-relaxed">
                                <div className="font-bold text-foreground">{projectLocationInfo.name}</div>
                                {projectLocationInfo.addressLines.map((line) => (
                                  <div key={line}>{line}</div>
                                ))}
                                {mode !== 'view' && (
                                  <button
                                    type="button"
                                    className="text-sm text-[rgb(59,151,176)] hover:text-[rgb(59,151,176)] font-bold underline block mt-2"
                                    onClick={() => void openClientEditModal(0, projectClientForEstimate || activeClient)}
                                    disabled={clientEditLoading}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Column 3: Estimate Controls */}
                        <div className="space-y-5 p-5 rounded-xl">
                          <div className="flex items-center gap-4">
                            <div className="space-y-1 flex-1">
                              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Estimate No.</label>
                              {mode === 'view' || mode === 'edit' ? (
                                <div className="text-[20px] font-bold text-foreground">
                                  {currentEstimateParts.versionedNumber || '—'}
                                </div>
                              ) : (
                                <Input
                                  value={baseNumber}
                                  className="h-10 font-bold text-[16px]"
                                  onChange={(event) => {
                                    setBaseNumber(sanitizeEstimateBaseNumber(event.target.value));
                                    setBaseNumberError('');
                                  }}
                                  placeholder={context?.nextBaseNumber || '1000'}
                                />
                              )}
                              {baseNumberError && <div className="text-[12px] text-destructive font-bold">{baseNumberError}</div>}
                            </div>
                          </div>

                          <BuilderDateField
                            label="Estimate Date"
                            value={draft.estimateDate}
                            editable={mode !== 'view'}
                            onChange={(value) => setDraftField('estimateDate', value)}
                          />
                          <BuilderDateField
                            label="Expiration Date"
                            value={draft.expirationDate}
                            editable={mode !== 'view'}
                            onChange={(value) => setDraftField('expirationDate', value)}
                          />
                        </div>

                      </div>
                    </div>

                    {mode === 'view' ? (
                      <div>{previewPanel}</div>
                    ) : isMobile ? (
                      <div className="px-4 py-4 md:px-6">
                        <Tabs value={mobileTab} onValueChange={(value) => setMobileTab(value as 'select' | 'preview')}>
                          <TabsList className="mb-4 grid h-9 w-full grid-cols-2 rounded-md bg-muted p-1">
                            <TabsTrigger value="select">Select Items</TabsTrigger>
                            <TabsTrigger value="preview">Preview ({selectedLinesCount})</TabsTrigger>
                          </TabsList>
                          <TabsContent value="select" className="mt-0">
                            <div className="h-[calc(100vh-420px)] overflow-hidden rounded-md border border-input">{selectorPanel}</div>
                          </TabsContent>
                          <TabsContent value="preview" className="mt-0">
                            <div className="h-[calc(100vh-420px)] overflow-hidden rounded-md border border-input">{previewPanel}</div>
                          </TabsContent>
                        </Tabs>
                      </div>
                    ) : (
                      <div className="grid grid-cols-[340px_minmax(0,1fr)] items-start border-b border-input">
                        {selectorPanel}
                        {previewPanel}
                      </div>
                    )}

                    <div className="border-t border-input px-4 py-5 md:px-6">
                      {mode === 'view' ? (
                        <div className="space-y-5">
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-foreground">Payment Terms</div>
                            <div className="text-sm whitespace-pre-wrap text-muted-foreground">{draft.paymentTerms || '—'}</div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-foreground">Terms & Conditions</div>
                            <div className="text-sm whitespace-pre-wrap text-muted-foreground">{draft.termsAndConditions || '—'}</div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-foreground">Note to Customer</div>
                            <div className="text-sm whitespace-pre-wrap text-muted-foreground">{draft.noteToCustomer || '—'}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <div className="text-sm font-semibold text-foreground">Payment Terms</div>
                            <Textarea
                              value={draft.paymentTerms}
                              className="min-h-[70px] py-1"
                              onChange={(event) => setDraftField('paymentTerms', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-semibold text-foreground">Terms & Conditions</div>
                            <Textarea
                              value={draft.termsAndConditions}
                              className="min-h-[70px] py-1"
                              onChange={(event) => setDraftField('termsAndConditions', event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-semibold text-foreground">Note to Customer</div>
                            <Textarea
                              value={draft.noteToCustomer}
                              className="min-h-[70px] py-1"
                              onChange={(event) => setDraftField('noteToCustomer', event.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 border-t border-input bg-white px-4 py-3 md:px-6">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        {mode !== 'create' ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteDialogOpen(true)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {mode === 'view' ? (
                          <>
                            <Button type="button" variant="outline" onClick={handleEnterEdit}>
                              Edit
                            </Button>
                            <Button type="button" onClick={() => openSendDialog('send')}>
                              <Send className="h-4 w-4" />
                              Send
                            </Button>
                          </>
                        ) : null}

                        {mode === 'edit' ? (
                          <>
                            <Button type="button" variant="ghost" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                            <Button type="button" variant="outline" onClick={() => validateBeforeSave() && setSaveDialogOpen(true)} disabled={!canSaveSelection}>
                              Save
                            </Button>
                            <Button type="button" onClick={() => canSaveSelection && openSendDialog('saveAndSend')} disabled={!canSaveSelection}>
                              <Mail className="h-4 w-4" />
                              Save & Send
                            </Button>
                          </>
                        ) : null}

                        {mode === 'create' ? (
                          <>
                            <Button type="button" variant="ghost" onClick={handleClose}>
                              Cancel
                            </Button>
                            <Button type="button" variant="outline" onClick={() => validateBeforeSave() && setSaveDialogOpen(true)} disabled={!canSaveSelection}>
                              Save
                            </Button>
                            <Button type="button" onClick={() => canSaveSelection && openSendDialog('saveAndSend')} disabled={!canSaveSelection}>
                              <Mail className="h-4 w-4" />
                              Save & Send
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Save Estimate</DialogTitle>
            <DialogDescription className="[overflow-wrap:anywhere] break-words">
              {existingEstimate
                ? `This will save as version ${footerVersionedNumber}.`
                : `This will create estimate EST ${sanitizeEstimateBaseNumber(baseNumber || context?.nextBaseNumber || '') || context?.nextBaseNumber || ''}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {saveDialogVersionSummary ? (
              <div className="rounded-md border border-input bg-muted/30 px-4 py-3 text-lg font-semibold text-foreground [overflow-wrap:anywhere] break-words">
                {saveDialogVersionSummary}
              </div>
            ) : null}
            {!existingEstimate ? (
              <div className="space-y-2">
                <div className="text-sm font-medium text-foreground">
                  Estimate Name <span className="text-muted-foreground font-normal">(optional)</span>
                </div>
                <Input
                  value={internalName}
                  onChange={(event) => handleInternalNameChange(event.target.value)}
                  maxLength={ESTIMATE_NAME_MAX_LENGTH}
                  placeholder="e.g. Summer 2026, Phase 1..."
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">
                Internal Note <span className="text-muted-foreground font-normal">(optional)</span>
              </div>
              <Textarea
                value={internalNote}
                onChange={(event) => setInternalNote(event.target.value)}
                placeholder="What changed in this version..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="flex h-[min(900px,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-[680px] flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-input px-5 py-4 pr-12">
            <DialogTitle>Send Estimate</DialogTitle>
            <DialogDescription>
              {sendDialogMode === 'send'
                ? 'Send this estimate to your client via email.'
                : 'Save this estimate and send it to your client via email.'}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-4">
              <div className="rounded-md border border-input px-4 py-3 text-lg font-semibold text-foreground">
                {sendDialogMode === 'send'
                  ? currentEstimateParts.versionedNumber
                  : buildVersionedNumber(
                    sanitizeEstimateBaseNumber(baseNumber || context?.nextBaseNumber || ''),
                    existingEstimate ? currentEstimateParts.version + 1 : 1
                  )}{' '}
                — {formatDate(draft.estimateDate)}
              </div>

              <EmailChipInput
                label="Client Email(s)"
                values={emailDraft.to}
                onChange={(values) => setEmailDraft((prev) => ({ ...prev, to: values }))}
                placeholder="Add email and press Enter..."
                disabled={sending}
              />

              <EmailChipInput
                label="CC"
                description="Additional recipients who will be copied on this email."
                values={emailDraft.cc}
                onChange={(values) => setEmailDraft((prev) => ({ ...prev, cc: values }))}
                placeholder="Add email and press Enter..."
                disabled={sending}
              />

              <EmailChipInput
                label="Reply-To"
                values={emailDraft.replyTo}
                onChange={(values) => setEmailDraft((prev) => ({ ...prev, replyTo: values }))}
                placeholder="Add email and press Enter..."
                disabled={sending}
              />

              {sendDialogMode === 'saveAndSend' ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">Internal Note</div>
                  <Textarea
                    value={internalNote}
                    onChange={(event) => setInternalNote(event.target.value)}
                    placeholder="Quick summary of new changes"
                    disabled={sending}
                  />
                </div>
              ) : null}

              {sendDialogMode === 'saveAndSend' && !existingEstimate ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">Estimate Name</div>
                  <Input
                    value={internalName}
                    onChange={(event) => handleInternalNameChange(event.target.value)}
                    maxLength={ESTIMATE_NAME_MAX_LENGTH}
                    placeholder="Phase 1"
                    disabled={sending}
                  />
                </div>
              ) : null}

              <div className="rounded-md border border-input p-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-foreground">Subject</div>
                    <Input
                      value={emailDraft.subject}
                      onChange={(event) => setEmailDraft((prev) => ({ ...prev, subject: event.target.value }))}
                      disabled={sending}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-foreground">Body</div>
                    <Textarea
                      className="min-h-[220px] whitespace-pre-wrap"
                      value={emailDraft.body}
                      onChange={(event) => setEmailDraft((prev) => ({ ...prev, body: event.target.value }))}
                      disabled={sending}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-input px-5 py-3">
            <Button type="button" variant="ghost" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSend()}
              disabled={sending || !normalizeEmailList(emailDraft.to).length}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sendDialogMode === 'send' ? 'Send' : 'Save & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Delete Estimate</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this estimate? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="delete" onClick={() => void handleDelete()} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {modalClient?.id ? (
        <ClientModal
          key={`estimate-client-${modalClient.id}-${clientEditTabIndex}`}
          client={modalClient}
          isModalOpen={clientEditModalOpen}
          onModalClose={() => {
            setClientEditModalOpen(false);
            setClientEditTarget(null);
          }}
          onClientSaved={handleClientSaved}
          resetOnClose
          setFocusFirstField={(fn) => (setFocusFirstFieldOnClientModalRef.current = fn)}
          tabIndex={clientEditTabIndex}
        />
      ) : null}

      <EstimateModal
        accountId={Number(accountId || localStorage.getItem('accountId') || 0)}
        isModalOpen={exportSuccessModalOpen}
        estimateId={exportedQbEstimateId || ''}
        qbEstimateDocNumber={exportedQbEstimateDocNumber || ''}
        onClose={() => setExportSuccessModalOpen(false)}
      />
      <BundleSetupModal
        className="quickbooks-bundle-setup-modal"
        uiActive={bundleSetupModalOpen}
        uiOnRequestClose={handleCloseBundleSetupModal}
        uiCloseOnEsc
        uiCloseOnOutsideClick
        onRetry={handleRetryBundleExport}
        onExportWithoutBundling={handleExportWithoutBundling}
        missingItems={bundleSetupMissingItems}
      />
    </>
  );
};

export default EstimateBuilderSheet;
